/* ----------------------------------------------------------------------------
 * useVoiceController — wires the pure state machine to the browser edge and to
 * the EXISTING AI brain. This is the ONLY place voice touches DOM/provider
 * adapters; it is type-checked by the app build (not run by the node tests).
 *
 * REUSE (research doc §0): the "brain" is the EXISTING Copilot AI Gateway — the
 * committed utterance is sent through `assist()` (POST /v1/ai/assist), NOT a
 * new AI pathway. STT/TTS are provider-neutral adapters. No external action is
 * ever taken without an explicit APPROVE.
 *
 * AUDIO PLAYBACK (bug fix 2026-07-18): Aura now actually SPEAKS. Synthesized
 * bytes are buffered then played through AuraAudioPlayer, which is UNLOCKED on
 * the push-to-talk gesture (browsers block autoplay without a user gesture — the
 * hold-to-speak press provides one). If the browser still blocks playback, the
 * controller exposes needsPlaybackTap + replay() so the UI can offer a
 * "tap to play" control instead of failing silently.
 * -------------------------------------------------------------------------- */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { assist, isDomainChoice, isBackendTurnClosed, type AssistProposalResponse } from '@/data/copilot';
import { api, ApiError } from '@/lib/api';
import {
  matchModuleAnswer,
  resolveClarifyAnswer,
  TURN_CLOSE_ACK,
  type BriefingModule,
} from './wake/wakeMachine.ts';
import { ConversationTurn } from './wake/turnEntity.ts';
import {
  PUBLISH_CONFIRM_QUESTION,
  PUBLISH_CONFIRM_REASK,
  PUBLISH_CONFIRM_CANCEL_ACK,
  PUBLISH_CONFIRM_QUEUED_ACK,
  PUBLISH_CONFIRM_EDIT_ACK,
  matchPublishConfirmAnswer,
  resolvePublishConfirmAnswer,
  buildPublishConfirmAction,
  type PublishConfirmDraft,
} from './actions/publishConfirmModel.ts';
// Aura publish P1 (owner decision 10-10-2026): batch Draft/Publish by voice/chat/tap.
import { useBatchPublishVoice, type BatchPublishDeps } from './actions/useBatchPublishVoice.ts';
import { defaultBatchPublishDeps } from './actions/batchPublishDeps.ts';
import { canManagePublishing, detectBatchPublishAsk, type BatchActiveDomain, type BatchMode, type BatchPublishView } from './actions/batchPublishModel.ts';
import { useOptionalAuth } from '@/auth/AuthContext';
// Site/action intent fix (owner report 10-10-2026 18:48 UTC): publish sites are
// scoped to the domain in the header ("Viewing: <domain>") — the server's active domain.
import { useActiveDomain } from '@/hooks/useActiveDomain';
// Aura P2 (owner GO 10-10-2026): "which 3 articles should we write?" -> cards -> write -> P1 hand-off.
import { useSuggestWriteVoice, type SuggestWriteDeps } from './actions/useSuggestWriteVoice.ts';
import { defaultSuggestWriteDeps } from './actions/suggestWriteDeps.ts';
import { useRecommendVoice, type RecommendView } from './actions/useRecommendVoice.ts';
import { detectSuggestWriteAsk, type SuggestWriteView } from './actions/suggestWriteModel.ts';
import { detectPendingListAsk, detectRecommendAsk } from './actions/recommendModel.ts';
import {
  voiceReducer,
  initialVoiceState,
  isActionAuthorised,
  buildRetainedResponse,
  canReplayLast,
  replayPlan,
  EMPTY_TRANSCRIPT_REPROMPT,
  resolveDeferredRelease,
  type VoiceState,
  type RetainedResponse,
} from './state/machine.ts';
import { isStale } from './state/asyncGuard.ts';
import {
  LISTEN_RECORD_CAP_MS,
  STT_FINALIZE_TIMEOUT_MS,
  STT_TIMEOUT_ERROR,
  listenTimeoutAction,
} from './listenGuard.ts';
import type { SttAdapter, SttStream } from './stt/SttAdapter.ts';
import type { TtsAdapter } from './tts/TtsAdapter.ts';
import { MockSttStream } from './stt/MockSttAdapter.ts';
import {
  createSttAdapter,
  createTtsAdapter,
  DEFAULT_VOICE_PROVIDER_CONFIG,
  type VoiceProviderConfig,
} from './providers/registry.ts';
import {
  AuraAudioPlayer,
  createBrowserAudioPrimitive,
  type AudioPrimitive,
} from './audio/AuraAudioPlayer.ts';
import { DEFAULT_AURA_PERSONA, type AuraPersona, type ProposedAction, type VoiceError } from './types.ts';
import { SegmentedNarrator } from './audio/SegmentedNarrator.ts';
import { fetchBriefing, type BriefingResponse } from './net/voiceApi.ts';
import {
  initVoiceDiag,
  diagSend,
  diagAssistResult,
  diagStateTransition,
  diagTtsSegment,
  diagAudioCtx,
  diagError,
  diagRecorderRestart,
  diagSttResult,
  voiceBeacon,
} from './net/voiceDiag.ts';
import type { BriefingSection, NarrationSegment } from './briefing/contract.ts';
import { selectWakePrompt, WAKE_ANSWERING_BACKSTOP_MS, WAKE_GREETING_URL } from './wake/handsFreeConfig.ts';
import { mergeWakeRemainder, stripWakeEcho } from './wake/wakeRemainder.ts';
import { wakeChimeBytes, WAKE_CHIME_MIME, WAKE_CHIME_MAX_WAIT_MS } from './wake/wakeChime.ts';
import type { WakePrompt } from './wake/handsFreeConfig.ts';

export interface UseVoiceControllerOptions {
  config?: VoiceProviderConfig;
  persona?: AuraPersona;
  /** active engine key for Copilot context (e.g. from the route). */
  engine?: string | null;
  /** dependency-injection seam for tests/demo (defaults to the registry). */
  sttAdapter?: SttAdapter;
  ttsAdapter?: TtsAdapter;
  /** dependency-injection seam for audio playback (defaults to the browser sink). */
  audioPrimitive?: AudioPrimitive;
  /** Pre-GRANT the mic permission on panel-open (best-effort getUserMedia then
   *  release) so the FIRST press never races the permission prompt. Pre-grant
   *  only: does NOT hold the mic, does NOT re-introduce warm-mic reuse. */
  prewarmMic?: boolean;
  /** #7: open the EXISTING draft editor surface when the publish-confirm loop
   *  hears "edit". The host wires this to the existing ContentActionPanel /
   *  BlogDraftView editor; the controller never owns the editor UI. */
  onOpenDraftEditor?: (draft: PublishConfirmDraft) => void;
  /** Aura publish P1: network deps for the batch Draft/Publish flow (tests inject fakes). */
  batchPublishDeps?: BatchPublishDeps;
  /** C6b: override for "may this human use batch publishing?" (tests). Default = the
   *  active workspace role from AuthContext is owner or operator; no auth = no. */
  canManagePublishing?: () => boolean;
  /** Override for the domain being viewed (tests). Default = the shell's active
   *  domain (useActiveDomain: the same source as the "Viewing: <domain>" header). */
  activeDomain?: () => BatchActiveDomain | null;
  /** Aura P2: network deps for the suggest + write flow (tests inject fakes). */
  suggestWriteDeps?: SuggestWriteDeps;
}

export interface VoiceController {
  state: VoiceState;
  persona: AuraPersona;
  /** the last spoken advisory text (for the transcript panel). */
  auraSpeech: string;
  /** true only when an approved action is ready for the caller to execute. */
  actionReady: boolean;
  approvedAction: ProposedAction | null;
  /** true when the browser blocked autoplay and a manual tap is needed to hear Aura. */
  needsPlaybackTap: boolean;
  /** user-gesture retry of the last (blocked) reply audio. */
  replay: () => Promise<void>;
  /** true when a last spoken response is retained AND Aura is idle (Replay shown). */
  canReplay: boolean;
  /** re-play the retained last response through the existing playback path — no
   *  new /v1/ai call (the TTS cache serves the identical audio). */
  replayLast: () => Promise<void>;
  /** Push-to-talk: call on hold-begin (mouse/touch down, Space keydown). */
  pressToTalk: () => Promise<void>;
  /** Push-to-talk: call on release (mouse/touch up/leave, Space keyup). */
  releaseToTalk: () => Promise<void>;
  /** Push-to-talk: call on touch/pointer CANCEL — abort the hold, send nothing. */
  cancelToTalk: () => void;
  stop: () => void;
  mute: () => void;
  unmute: () => void;
  interrupt: () => void;
  submitText: (text: string) => Promise<void>;
  requestApproval: (action: ProposedAction) => void;
  approve: () => void;
  reject: () => void;
  clearApprovedAction: () => void;
  /** the last briefing's display sections (null until a briefing is presented). */
  auraSections: BriefingSection[] | null;
  /** true while the daily briefing is being fetched (before narration starts). */
  briefingLoading: boolean;
  /** fetch + present the daily briefing: render sections + speak the paced
   *  narration segments. Call from a user gesture (unlocks audio playback). */
  briefMe: (greetingName?: string) => Promise<void>;
  /** present an already-fetched briefing (render sections + speak segments). */
  presentBriefing: (res: BriefingResponse) => Promise<void>;
  /** speak an ordered list of narration segments with client-timed gaps. */
  speakSegments: (segments: NarrationSegment[]) => Promise<void>;
  /** local-demo helper (mock STT only): feed a spoken phrase. */
  demoSay: (text: string) => void;
  /** HANDS-FREE: publish/withdraw the shared wake mic so STT reuses it (no 2nd getUserMedia). */
  setSharedMicStream: (stream: MediaStream | null) => void;
  /** HANDS-FREE: reflect the wake-armed flag into VoiceState (drives the wake-armed visual). */
  setWakeArmed: (armed: boolean) => void;
  /** PTT-COEXIST (#2.2 P3): hands-free host registers a callback fired on every
   *  manual press so the wake flow PARKS for the turn (never torn down). */
  setPttObserver: (cb: (() => void) | null) => void;
  /** HANDS-FREE: play the cached greeting, then start the EXISTING Listening/STT capture. */
  beginWakeTurn: () => Promise<void>;
  /** HANDS-FREE: end capture -> existing STT -> existing assist -> existing TTS reply.
   *  Returns 'clarify' when the turn asked a "which module?" question and a follow-up
   *  answer capture is now OPEN (the caller must NOT re-arm); 'empty' when the capture
   *  endpointed with NO usable STT result (empty/fragment transcript or an STT
   *  transport failure -- voice residual #2, the empty-capture refractory signal);
   *  or 'done' for a normal turn with real content. */
  endWakeCommand: () => Promise<WakeTurnOutcome>;
  /** HANDS-FREE (iOS): unlock/prime the shared AudioContext INSIDE the user's
   *  Enable tap (a real gesture) so the wake-triggered greeting is not autoplay-
   *  blocked later. The post-wake unlock in beginWakeTurn remains as a fallback. */
  primeAudioForHandsFree: () => void;
  /** #7: start the post-prep publish-confirm voice loop for a prepared draft.
   *  Aura asks "shall I publish this draft?" and captures a CLOSED-SET yes/edit/no
   *  answer (PTT + hands-free). 'yes' parks the draft in the EXISTING owner-gated
   *  approval card (NO publish leg); 'edit' fires onOpenDraftEditor; 'no'/close
   *  cancels cleanly. Follows the clarify pattern — never via the wake 'armed' phase. */
  beginPublishConfirm: (draft: PublishConfirmDraft) => Promise<void>;
  /** Aura publish P1: the on-screen batch cards ("push the next N drafts to <site>"),
   *  or null. Rendered by BatchPublishPanel; never read out. */
  batchPublish: BatchPublishView | null;
  /** Tap/tick confirm: Draft or Publish for one card (draftIds) or all pending cards. */
  confirmBatchPublish: (mode: BatchMode, draftIds?: number[]) => Promise<void>;
  chooseBatchSite: (siteId: number) => Promise<void>;
  undoBatchPublish: (publicationId: number, draftId: number) => Promise<void>;
  dismissBatchPublish: () => void;
  /** Connector 1.3.0 update_live (owner GO 12-10-2026 15:53 UTC, option A): tap paths. */
  updateLivePost: (draftId: number) => Promise<void>;
  confirmUpdateLivePost: () => Promise<void>;
  cancelUpdateLivePost: () => Promise<void>;
  undoUpdateLivePost: () => Promise<void>;
  /** Aura P2: the on-screen keyword article suggestions, or null. Never read out. */
  suggestWrite: SuggestWriteView | null;
  /** Tap "Write them": writes the shown cards as BookedCentral drafts, then the P1 cards take over. */
  writeSuggested: () => Promise<void>;
  /** tap "Write this one" on a suggestion card (the tap is the confirmation). */
  writeSuggestedOne: (index: number) => Promise<void>;
  chooseSuggestSite: (siteId: number) => Promise<void>;
  dismissSuggestWrite: () => void;
  /** conversational-actions: Aura's numbered recommend / pending list (tap = pick). */
  recommend: RecommendView | null;
  pickRecommended: (n: number) => Promise<void>;
  pickAllRecommended: () => Promise<void>;
  chooseRecommendSite: (siteId: number) => Promise<void>;
  dismissRecommend: () => void;
  /** Phase C (self-trigger fix): TRUE while Aura's own TTS audio is physically
   *  in flight OR within its short post-playback grace window. The
   *  HandsFreeBridge reads this so the wake reducer's re-arm gate has ONE
   *  true signal instead of a bare VoiceStatus flip (see turnEntity.ts
   *  isAnsweringOrGrace / handsFreeConfig.ts POST_ANSWER_SCORE_GRACE_MS). */
  isAnswering: () => boolean;
  /** C2 (stop-fix IVG 10-10-2026, aura-stop-fix-ivg-2026-10-10_1451.md): the
   *  hands-free glue calls this on the diag-OFF local no-speech discard (the
   *  primary wake capture heard no speech, so STT never runs and onFinal /
   *  quiet_close are never reached). A QUIET (capped, F3) hands-free turn is
   *  closed here exactly as the diag-ON quiet_close does: no speech, close()
   *  resets hasWoken + the follow-up cap and starts the post-close cooldown.
   *  Any other turn is left untouched (no-op). */
  noSpeechDiscard: () => void;
  /** WAKE CASCADE P1 (owner GO "go phase 1", 10-10-2026): open the request
   *  capture SILENTLY on a local detector fire -- no chime, no prompt -- while
   *  the server confirms the wake phrase. Same cooldown/turn rules as
   *  beginWakeTurn; the turn is QUIET. */
  beginSilentWakeTurn: () => Promise<void>;
  /** WAKE CASCADE P1: the server confirmed "Aura" -- play the chime NOW (the
   *  capture is already open). Never speaks the follow-up prompt (it would play
   *  over the user's request: owner report 10-10-2026 ~17:05 UTC). */
  confirmWakeTurn: (opts?: { greet?: boolean; afterWake?: string | null }) => Promise<void>;
  /** WAKE CASCADE P1: not confirmed -- end the hands-free turn WITHOUT the
   *  post-close cooldown (a false fire must not block a real "Hey Aura").
   *  The caller then stop()s, which discards the capture (no STT). */
  rejectWake: () => void;
}

/** Outcome of a hands-free wake turn: 'clarify' => a "which module?" question was
 *  asked and the follow-up answer capture is OPEN (do NOT re-arm); 'empty' => the
 *  capture endpointed with NO usable STT result (empty/fragment transcript or an STT
 *  transport failure) -- voice residual #2's empty-capture signal, consumed by
 *  useHandsFree's EmptyCaptureRefractory; 'done' => the turn is complete with real
 *  content (re-arm normally on the next STATUS idle). */
export type WakeTurnOutcome = 'clarify' | 'done' | 'empty';

function toVoiceError(err: unknown): VoiceError {
  // Owner cap-scope decision (10-10-2026 18:28 UTC): monthly voice limit -> the
  // exact owner-facing notice (typed chat keeps working).
  if (err instanceof Error && err.name === 'VoiceSttCapError') {
    return { code: 'stt_failed', message: err.message };
  }
  if (err instanceof ApiError) {
    return { code: err.status === 0 ? 'network' : 'reasoning_failed', message: 'Aura could not complete that just now.' };
  }
  return { code: 'unknown', message: 'Something went wrong. Please try again.' };
}

/** Monotonic-ish millisecond clock for beacon timing (perf.now when present). */
function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** Concatenate streamed audio chunks into a single decodable clip. */
function concatChunks(chunks: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.byteLength;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

/** RAW capture (owner directive 2026-07-23, MEASUREMENT): request the mic with
 *  Android WebRTC audio processing OFF. readCaptureProfile logs REQUESTED
 *  (getConstraints) AND ACTUAL (getSettings) so we see if Android honoured it. */
const RAW_AUDIO_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
};

/** F7: never let a missing 'ended' event hold a lone-wake turn (the asset is ~1 s). */
const WAKE_GREETING_MAX_WAIT_MS = 2500;
/** F9: silence kept after the greeting ends before the capture opens. */
export const WAKE_GREETING_TAIL_GUARD_MS = 350;

export function useVoiceController(options: UseVoiceControllerOptions = {}): VoiceController {
  const config = options.config ?? DEFAULT_VOICE_PROVIDER_CONFIG;
  const persona = options.persona ?? DEFAULT_AURA_PERSONA;
  const [state, dispatch] = useReducer(voiceReducer, undefined, initialVoiceState);

  const stt = useMemo<SttAdapter>(() => options.sttAdapter ?? createSttAdapter(config.stt), [options.sttAdapter, config.stt]);
  const tts = useMemo<TtsAdapter>(() => options.ttsAdapter ?? createTtsAdapter(config.tts), [options.ttsAdapter, config.tts]);

  // One player per controller instance. The browser primitive is lazy + DOM-safe.
  const playerRef = useRef<AuraAudioPlayer | null>(null);
  if (playerRef.current === null) {
    playerRef.current = new AuraAudioPlayer(options.audioPrimitive ?? createBrowserAudioPrimitive());
  }
  const player = playerRef.current;

  // Briefing/assist narration as ONE natural utterance (single synth of the
  // joined text, single play, no client gaps + barge-in) (owner directive 2026-07-19).
  const narratorRef = useRef<SegmentedNarrator | null>(null);
  // The EXACT audio bytes of the last spoken reply, kept so Replay plays them
  // INSTANTLY with no re-synthesis / no /v1 call / no provider cost (owner 2026-07-19).
  const lastAudioRef = useRef<{ bytes: Uint8Array; mime: string } | null>(null);
  const currentSynthRef = useRef<{ cancel: () => void } | null>(null);
  const sleepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sleepResolveRef = useRef<(() => void) | null>(null);
  const [auraSections, setAuraSections] = useState<BriefingSection[] | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Monotonic turn/epoch: bumped on every new turn (press / submit / barge-in)
  // AND on stop(). An async continuation captures the epoch it began under and
  // BAILS if the epoch has since moved — so no late STT/think/speak can restart
  // Aura after Stop (owner-reported stop-stuck fix, 2026-07-19).
  const epochRef = useRef(0);
  const nextEpoch = useCallback(() => {
    epochRef.current += 1;
    // Fable C-AO1: a wake remainder belongs to ONE turn; a new turn never sees it.
    wakeRemainderRef.current = null;
    return epochRef.current;
  }, []);

  // ---- HANDS-FREE scoped-briefing CLARIFY turn state (hands-free ONLY) --------
  // Phase B (voice-turn-audio-redesign, 2026-07-27): turnRef is ONE conversation-
  // turn entity (wake/turnEntity.ts) carrying what used to be independent
  // parallel refs — handsFreeTurn gates the briefing-intent trigger; subState
  // (kind 'clarify') carries the offered menu + re-ask counter while awaiting a
  // spoken module answer (was: clarifyCtxRef); turnOutcomeRef (below, unchanged)
  // lets endWakeCommand await the turn DECISION (clarify-capture vs done) so the
  // follow-up capture opens WITHOUT re-arming.
  const turnRef = useRef(new ConversationTurn());
  // 'empty' (voice residual #2): the capture endpointed with NO usable STT result
  // (empty/fragment transcript, or an STT transport failure) -- distinct from 'done'
  // (real content) so endWakeCommand can report it to the wake glue's empty-capture
  // refractory without touching the clarify-capture handshake above.
  const turnOutcomeRef = useRef<{ resolve: (o: 'clarify-capture' | 'done' | 'empty') => void; promise: Promise<'clarify-capture' | 'done' | 'empty'> } | null>(null);
  const armTurnOutcome = useCallback(() => {
    let resolve!: (o: 'clarify-capture' | 'done' | 'empty') => void;
    const promise = new Promise<'clarify-capture' | 'done' | 'empty'>((r) => { resolve = r; });
    turnOutcomeRef.current = { resolve, promise };
  }, []);
  const settleTurn = useCallback((o: 'clarify-capture' | 'done' | 'empty') => {
    turnOutcomeRef.current?.resolve(o);
  }, []);

  // ---- #7 POST-PREP publish-confirm loop state (PTT + hands-free) ------------
  // Phase B: the "shall I publish this draft?" sub-state now lives on the SAME
  // turnRef entity (subState kind 'publish_confirm') instead of a second,
  // independently-mutated parallel ref (was: publishConfirmCtxRef) — see
  // wake/turnEntity.ts TurnSubState. askPublishConfirmRef indirects to the
  // (later-defined) capture-reopen so the re-ask fires without a forward dependency.
  const askPublishConfirmRef = useRef<((reask: boolean) => Promise<void>) | null>(null);

  // Retained LAST spoken response (segments + display) so it can be REPLAYED
  // with NO new /v1/ai call — replaced whenever a new response is produced.
  // `retained` (state) drives the Replay button visibility re-render; the ref
  // keeps the replay callback free of a stale closure.
  const [retained, setRetained] = useState<RetainedResponse | null>(null);
  const retainedRef = useRef<RetainedResponse | null>(null);
  const retain = useCallback((segments: NarrationSegment[], text: string) => {
    const next = buildRetainedResponse(segments, text);
    if (next === null) return;
    retainedRef.current = next;
    setRetained(next);
  }, []);

  /** Barge-in for the briefing narration: invalidate the whole remaining segment
   *  queue immediately, abort in-flight synthesis, and end any pending gap. */
  const cancelNarration = useCallback((reason: string = 'unspecified') => {
    // Aura brevity (briefing stopped after 2 of 4 segments, 10-10-2026 16:11/16:15):
    // name WHO cancelled a live narration -- content-free diagnostics only.
    if (narratorRef.current?.isPlaying()) voiceBeacon('narration_cancel', { reason });
    narratorRef.current?.cancel();
    try { currentSynthRef.current?.cancel(); } catch { /* no-op */ }
    currentSynthRef.current = null;
    if (sleepTimerRef.current !== null) {
      clearTimeout(sleepTimerRef.current);
      sleepTimerRef.current = null;
    }
    const resolve = sleepResolveRef.current;
    sleepResolveRef.current = null;
    resolve?.();
    // IVG FINDING-1 FIX (2026-07-27): cancelNarration is THE single choke
    // point every user-facing narration-terminal cancel funnels through
    // (stop(), pressToTalk(), cancelToTalk(), interrupt(), briefMe(),
    // beginWakeTurn(), dispose) -- but SegmentedNarrator.cancel() bumps the
    // run token and play() then returns SILENTLY at its token checks, so
    // onEnded/onBlocked never fire and ConversationTurn.answering stayed
    // stuck true forever (STATUS idle held in the 250ms retry loop -> wake
    // scoring never re-armed -> hands-free dead until reload). Close the
    // answering window HERE, once, instead of sprinkling endAnswering over
    // every caller. endAnswering (not a hard clear) keeps the short
    // POST_ANSWER_SCORE_GRACE_MS tail -- correct, since a barge-stopped
    // speaker can still be emitting an audio tail at this instant.
    if (turnRef.current.answering) turnRef.current.endAnswering(Date.now());
  }, []);

  // Mirror the player's "needs a tap" state into React so the UI can re-render.
  const [needsPlaybackTap, setNeedsPlaybackTap] = useState(false);
  useEffect(() => {
    setNeedsPlaybackTap(player.getState().needsUserPlay);
    return player.subscribe((s) => setNeedsPlaybackTap(s.needsUserPlay));
  }, [player]);

  // PRE-GRANT the mic on panel-open (owner directive 2026-07-23): best-effort
  // getUserMedia then immediately RELEASE, so the permission prompt resolves
  // BEFORE the first press (no gesture-steal race). Permission pre-grant only.
  useEffect(() => {
    if (options.prewarmMic !== true) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;
    void navigator.mediaDevices
      .getUserMedia(RAW_AUDIO_CONSTRAINTS)
      .then((s) => s.getTracks().forEach((t) => t.stop()))
      .catch(() => { /* denied/unavailable — a real press re-requests + surfaces it */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once pre-grant
  }, []);

  const streamRef = useRef<SttStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  // HANDS-FREE: the wake engine's shared mic (BORROWED, not owned here). It is
  // used for STT so we never open a SECOND getUserMedia; kept OUT of micRef so
  // finish/stop never stop the wake mic (useHandsFree releases it on Disable).
  const sharedMicRef = useRef<MediaStream | null>(null);
  // True once the REAL mic permission has EVER been granted in this session. Used
  // to tell a genuine FIRST-permission grant (whose press gesture the OS prompt
  // may have stolen) apart from every normal subsequent use.
  const micEverGrantedRef = useRef(false);

  // Bounded-timeout guards so the LISTENING phase can NEVER hang (P0 2026-07-20,
  // replaces the retired mic-acquisition watchdog). recordCap: a missed release
  // can't record forever; finalizeGuard: a stalled STT round-trip can't hang on
  // "Sending…". Both single-shot via refs; NEITHER re-arms listening.
  const recordCapRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalizeGuardRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // FIRST vs FOLLOW-UP wake prompt (owner FIX 2, 2026-07-25): false until the
  // first "Hey Aura" greeting has played this session; thereafter the wake prompt
  // is the spoken follow-up ("Is there anything else?"). Persists for the
  // controller's life (HandsFreeEngineHost keeps ONE controller per app session).
  // FIRST vs FOLLOW-UP wake prompt (owner FIX 2, 2026-07-25) + POST-CLOSE
  // COOLDOWN (D4 fix, voice-wave-4): Phase B moved both onto turnRef as
  // entity-owned properties (turnRef.current.hasWoken / .closedAtMs /
  // .isInCooldown()) — was two separate refs (hasWokenRef, closedAtRef). See
  // wake/turnEntity.ts ConversationTurn.close()/isInCooldown().
  // Cached FOLLOW-UP prompt audio (owner FIX 2 EDGE b, 2026-07-25): the spoken
  // "Is there anything else?" is synthesised ONCE, then replayed from this ref on
  // every subsequent follow-up — mirroring greetingBytesRef, so no repeat synth.
  const followUpAudioRef = useRef<Uint8Array | null>(null);
  // F7: cached "How can I help?" asset bytes (lone-wake prompt).
  const greetingBytesRef = useRef<Uint8Array | null>(null);
  // F2: confirm-clip after-wake words, merged into the wake turn's first final.
  // Fable C-AO1 (10-10-2026): BOUND to the wake turn's epoch -- merged only into
  // a final of THAT turn; any epoch bump (PTT, stop, cancel, new wake) drops it.
  const wakeRemainderRef = useRef<{ epoch: number; text: string } | null>(null);
  const auraSpeechRef = useRef<string>('');
  const engine = options.engine ?? null;

  // Once-per-session greeting: the FIRST spoken response of THIS controller
  // instance (== one conversation/session) is greeted; later responses are not.
  // The client supplies its LOCAL time (the server computes the time-of-day +
  // the name and prepends "Good <tod>, <name>."). We never reset within the
  // instance — a fresh mount is a fresh session.
  const hasGreetedRef = useRef(false);
  const greetingSignal = useCallback(() => {
    let localHour: number | undefined;
    let tz: string | undefined;
    try {
      const h = new Date().getHours();
      if (Number.isInteger(h) && h >= 0 && h <= 23) localHour = h;
    } catch {
      /* no-op — absent local time => the server simply omits the greeting */
    }
    try {
      const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (typeof z === 'string' && z !== '') tz = z;
    } catch {
      /* no-op */
    }
    return { greet: !hasGreetedRef.current, localHour, tz };
  }, []);

  // Always-fresh view of the machine state for async callbacks (avoids stale
  // closures around cancelTts / muted during a long TTS stream).
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Diagnostics beacon (gated no-op unless the server reports diag=true). Read
  // the flag once on mount, then beacon every machine state transition so an
  // Android retest shows the exact status path (e.g. listening->thinking->error).
  useEffect(() => { initVoiceDiag(); }, []);
  const prevStatusRef = useRef(state.status);
  useEffect(() => {
    const from = prevStatusRef.current;
    const to = state.status;
    if (from !== to) {
      diagStateTransition(from, to, to === 'error' ? (state.error?.code ?? 'error') : undefined);
      prevStatusRef.current = to;
    }
  }, [state.status, state.error]);

  // Barge-in / stop: whenever the machine asks to cancel TTS, halt playback.
  useEffect(() => {
    if (state.cancelTts) player.stop();
  }, [state.cancelTts, player]);

  const releaseMic = useCallback(() => {
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    void streamRef.current?.close();
    streamRef.current = null;
  }, []);

  /** Resolve once the current clip stops playing (natural end / stop). */
  const waitForPlaybackEnd = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (!player.getState().playing) {
          resolve();
          return;
        }
        const unsub = player.subscribe((s) => {
          if (!s.playing) {
            unsub();
            resolve();
          }
        });
      }),
    [player],
  );

  const mimeForProvider = useMemo(() => (config.tts === 'mock' ? 'audio/wav' : 'audio/mpeg'), [config.tts]);

  const speak = useCallback(
    async (text: string) => {
      auraSpeechRef.current = text;
      // NOTE: we do NOT dispatch SPEAK here. The visible "speaking" state is
      // bound to the REAL start of audio (playBytes onStart) so the orb never
      // shows "speaking" in silence. Status stays "thinking" during synthesis.
      player.stop();

      const synth = tts.synthesize(text, {
        voice: config.ttsVoiceId,
        locale: persona.accent,
        provider: config.tts,
        providerVersion: 'beta',
      });

      const chunks: Uint8Array[] = [];
      try {
        for await (const chunk of synth.stream()) {
          if (stateRef.current.cancelTts) {
            synth.cancel();
            break;
          }
          if (chunk.bytes && chunk.bytes.byteLength > 0) chunks.push(chunk.bytes);
        }
      } catch {
        /* transport/synthesis error — fall through to end the speaking state */
      }

      // Barged-in / muted / nothing synthesized: return to idle honestly. We
      // never optimistically entered "speaking", so this leaves "thinking".
      if (stateRef.current.cancelTts || stateRef.current.muted || chunks.length === 0) {
        dispatch({ type: 'SPEAK_FAILED' });
        return;
      }

      // Bind "speaking" to the ACTUAL start of audio (onStart), never the request.
      // Phase C (self-trigger fix): beginAnswering()/endAnswering() bracket the
      // REAL playback window on the turn entity -- the wake reducer's ONE true
      // signal for "safe to re-arm scoring" (never a bare VoiceStatus flip).
      const outcome = await player.playBytes(concatChunks(chunks), mimeForProvider, {
        onStart: () => { turnRef.current.beginAnswering(); dispatch({ type: 'SPEAK' }); },
      });
      if (outcome === 'played') {
        await waitForPlaybackEnd();
        turnRef.current.endAnswering(Date.now());
        dispatch({ type: 'SPEAKING_ENDED' });
      } else {
        // 'blocked' keeps needsPlaybackTap true so the UI surfaces "tap to play";
        // 'error'/'skipped' fall back to idle. Either way: no silent "speaking".
        dispatch({ type: 'SPEAK_FAILED' });
      }
    },
    [tts, config.ttsVoiceId, config.tts, persona.accent, player, mimeForProvider, waitForPlaybackEnd],
  );

  // Speak a narration as ORDERED, PACED segments (owner directive 2026-07-19):
  // each display section is one segment, spoken as a continuous chunk, and the
  // narrator holds a REAL client-timed silence of the segment's pauseAfterMs
  // (700ms) AFTER it before the next — so the audio breathes at paragraph/topic
  // boundaries exactly like the printed layout, natural WITHIN a topic. The next
  // segment is PREFETCHED (synthesised) during the current playback + gap so the
  // pause never adds latency (the default-on TTS cache serves a repeat for free).
  // Reuses the SAME synth path + AuraAudioPlayer (gesture-unlock, tap-to-play,
  // speaking bound to real playback). A fresh narrator per run; barge-in cancels
  // the WHOLE queue (in-flight + prefetched) whole.
  const speakSegments = useCallback(
    async (segments: NarrationSegment[]) => {
      narratorRef.current?.cancel();
      player.stop();
      // Track whether ANY segment produced real audio this run. This decides how
      // a mid-queue block is reported to the machine: if we were already
      // "speaking", end speaking; if nothing ever played (e.g. the FIRST segment
      // was autoplay-blocked on mobile across the async gap, so onStart/SPEAK
      // never fired and status is still "thinking"), fail the turn from thinking.
      // Dispatching SPEAKING_ENDED from "thinking" is a no-op — THAT was the
      // mobile bug: the machine stuck in "thinking", the reply never surfaced,
      // and push-to-talk was blocked (it ignores holds while thinking).
      let spokeAny = false;
      let synthIdx = 0;
      let playIdx = 0;
      const narrator = new SegmentedNarrator({
        synthesize: async (text: string) => {
          const segIdx = synthIdx++;
          // 7b FIX (voice-wave-4, owner re-test-2 2026-07-27): a synth-START
          // beacon (previously only synth-DONE existed). The re-test-2 evidence
          // showed a flat ~2s "not synth-ready at the boundary" overhead at
          // EVERY inter-segment gap regardless of how long the prior segment
          // played (i.e. plenty of overlap window) -- the segmented-prefetch
          // design (this SAME concurrent kick-off, unit-proven in
          // segmented-prefetch.test.ts) says that should not happen. Without a
          // start timestamp there was no way to tell "prefetch started late"
          // apart from "this provider call is just slow" from evidence.log
          // alone. Diagnostics only -- zero behaviour change.
          voiceBeacon('tts_segment_synth_start', { index: segIdx, chars: text.length }); // P5: length only
          const synth = tts.synthesize(text, {
            voice: config.ttsVoiceId,
            locale: persona.accent,
            provider: config.tts,
            providerVersion: 'beta',
          });
          currentSynthRef.current = synth;
          const chunks: Uint8Array[] = [];
          try {
            for await (const chunk of synth.stream()) {
              if (chunk.bytes && chunk.bytes.byteLength > 0) chunks.push(chunk.bytes);
            }
          } catch {
            /* transport/synthesis error for this segment — skip it honestly */
          }
          currentSynthRef.current = null;
          const out = chunks.length > 0 ? concatChunks(chunks) : null;
          if (out) lastAudioRef.current = { bytes: out, mime: mimeForProvider };
          diagTtsSegment(segIdx, out ? 'ok' : 'fail');
          return out;
        },
        play: async (bytes, onStart) => {
          const outcome = await player.playBytes(bytes, mimeForProvider, { onStart });
          if (outcome === 'played') {
            // 7b FIX (voice-wave-4): this beacon previously fired at PLAYBACK
            // START (the instant player.playBytes() resolves -- the SAME moment
            // onStart() fires above), not at playback END, even though the
            // field is consumed downstream as "play_outcome=played" implying
            // "finished". That mislabeled a near-zero start-to-start span as the
            // segment's "playback duration" in evidence.log, which is the exact
            // measurement the re-test-2 defect-7 report built its (inconclusive)
            // time breakdown on. Wait for the REAL end before logging so a
            // future trace measures what it says it measures. Pure diagnostics
            // fix -- SegmentedNarrator's own subsequent waitForEnd() call
            // resolves instantly afterwards (idempotent), so no audio timing
            // changes.
            await waitForPlaybackEnd();
          }
          diagTtsSegment(playIdx++, 'ok', outcome);
          return outcome;
        },
        waitForEnd: () => waitForPlaybackEnd(),
        sleep: (ms) =>
          new Promise<void>((resolve) => {
            sleepResolveRef.current = resolve;
            sleepTimerRef.current = setTimeout(() => {
              sleepTimerRef.current = null;
              sleepResolveRef.current = null;
              resolve();
            }, ms);
          }),
        onSpeakStart: () => { spokeAny = true; turnRef.current.beginAnswering(); dispatch({ type: 'SPEAK' }); },
        onEnded: () => {
          voiceBeacon('narration_end', { reason: 'ended', played: playIdx, total: segments.length }); // P5
          turnRef.current.endAnswering(Date.now());
          dispatch({ type: 'SPEAKING_ENDED' });
        },
        // Autoplay-blocked / all-silent queue: leave the machine at idle HONESTLY
        // and keep needsPlaybackTap (surfaced by the player) so the UI offers
        // "tap to hear Aura" and keeps the transcript + reply on screen — never a
        // silent return to Ready, never a stuck "thinking". SPEAK_FAILED idles
        // from "thinking" (nothing spoke yet); SPEAKING_ENDED idles from
        // "speaking" (a later segment blocked after audio had started).
        onBlocked: () => {
          // Phase C: a mid-queue block after some segment(s) already played is
          // ALSO a real end of Aura's audio -- close the answering window here
          // too, not just on the clean onEnded path (see speak()'s comment).
          if (spokeAny) turnRef.current.endAnswering(Date.now());
          voiceBeacon('narration_end', { reason: 'blocked', played: playIdx, total: segments.length }); // P5
          dispatch({ type: spokeAny ? 'SPEAKING_ENDED' : 'SPEAK_FAILED' });
        },
        cancelSynthesis: () => { try { currentSynthRef.current?.cancel(); } catch { /* no-op */ } },
      });
      narratorRef.current = narrator;
      await narrator.play(segments);
    },
    [tts, config.ttsVoiceId, config.tts, persona.accent, player, mimeForProvider, waitForPlaybackEnd],
  );

  // Present an already-fetched briefing: render its sections and speak its paced
  // narration segments. Falls back to the flat `narration` text (one block) when
  // an older server returns no segments (back-compat).
  const presentBriefing = useCallback(
    async (res: BriefingResponse) => {
      const sections = Array.isArray(res.sections) && res.sections.length > 0 ? res.sections : null;
      setAuraSections(sections);
      const segments = Array.isArray(res.narration_segments)
        ? res.narration_segments.filter((seg) => seg && typeof seg.text === 'string' && seg.text.trim() !== '')
        : [];
      retain(
        segments,
        segments.length > 0
          ? segments.map((seg) => seg.text).join(' ')
          : typeof res.narration === 'string' ? res.narration : '',
      );
      if (segments.length > 0) {
        auraSpeechRef.current = segments.map((seg) => seg.text).join(' ');
        await speakSegments(segments);
      } else if (typeof res.narration === 'string' && res.narration.trim() !== '') {
        await speak(res.narration);
      }
    },
    [speakSegments, speak, retain],
  );

  // Fetch + present the daily briefing. Called from a user gesture, so it unlocks
  // audio playback first (so the paced narration is not autoplay-blocked).
  const briefMe = useCallback(
    async (greetingName?: string) => {
      void player.unlock();
      cancelNarration('brief_me');
      player.stop();
      setBriefingLoading(true);
      try {
        const sig = greetingSignal();
        const res = await fetchBriefing(greetingName, { greet: sig.greet, localHour: sig.localHour, tz: sig.tz });
        hasGreetedRef.current = true; // the briefing is a spoken response; greet once
        setBriefingLoading(false);
        await presentBriefing(res);
      } catch (err) {
        setBriefingLoading(false);
        const ve = toVoiceError(err);
        diagError(ve.code, (err as Error)?.message ?? ve.message, (err as Error)?.stack);
        dispatch({ type: 'ERROR', error: ve });
      }
    },
    [player, cancelNarration, presentBriefing, greetingSignal],
  );

  // SINGLE CLOSE CHOKE POINT (voice residual #1, 2026-07-28 -- HELD-ITEMS-
  // REGISTER-2026-07-23.md finding logged 2026-07-28 ~12:35): every path that
  // decides a turn is CLOSING -- the FE-detected close (classifyFinal returns
  // turn_close, consumed by handleTurnCloseWake below) AND the BE turn-close
  // BACKSTOP response (AiRoutes isTurnClose parity / defence-in-depth path,
  // consumed in think() below) -- MUST run through this ONE function, so
  // ConversationTurn.close() stamps closedAtMs (the 9s post-close cooldown,
  // isInCooldown()) identically regardless of which side detected the close.
  // Before this fix the BE-driven path never called turnRef.current.close()
  // at all -- a marginal wake re-fire right after a BE-acked close was never
  // suppressed (live evidence: a close phrase acked with zero
  // post_close_cooldown_suppressed beacons, versus the FE-detected close path
  // which stamps and suppresses re-fires correctly the same session).
  const closeTurnAndAck = useCallback(
    async (turnEpoch: number, ackText: string) => {
      settleTurn('done');
      turnRef.current.close(Date.now());
      // IVG F4 (pre-existing rule, now shared by both callers): a Stop or a
      // new turn during the close must not speak a stale ack.
      if (isStale(turnEpoch, epochRef.current)) return;
      auraSpeechRef.current = ackText;
      setAuraSections(null);
      try { await speak(ackText); } catch { /* ack is best-effort */ }
    },
    [speak, settleTurn],
  );

  // The "brain": reuse the EXISTING Copilot AI Gateway (POST /v1/ai/assist).
  const think = useCallback(
    async (utterance: string, turnEpoch: number) => {
      dispatch({ type: 'THINKING_STARTED' });
      try {
        const sig = greetingSignal();
      const gctx: Record<string, string | number | boolean | null> = { modality: 'voice' };
      if (sig.localHour !== undefined) gctx.client_local_hour = sig.localHour;
      if (sig.tz !== undefined) gctx.client_tz = sig.tz;
      if (sig.greet) gctx.greet = true;
      diagSend(utterance);
      const assistStart = nowMs();
      const res = await assist(utterance, { engine, context: gctx })
        .then((r) => { diagAssistResult(true, 200, Math.round(nowMs() - assistStart)); return r; })
        .catch((e: unknown) => {
          diagAssistResult(false, e instanceof ApiError ? e.status : 0, Math.round(nowMs() - assistStart));
          throw e;
        });
        // STALE-ASYNC GUARD: if Stop or a new turn happened while the model was
        // thinking, DISCARD this result — never let a late reply move Aura back
        // to thinking/speaking after the user stopped.
        if (isStale(turnEpoch, epochRef.current)) return;
        // BE TURN-CLOSE BACKSTOP (voice residual #1, 2026-07-28): the server
        // independently recognised a negative-intent close that the FE local
        // isTurnClose() vocab check did not catch on this turn. This response
        // shape carries no proposal/sections field -- it MUST be handled here,
        // BEFORE the res.proposal.text read below (which would otherwise throw
        // on this shape), and it MUST run the SAME close+cooldown as the
        // FE-detected path via closeTurnAndAck, never just speak the ack inline.
        if (isBackendTurnClosed(res)) {
          voiceBeacon('be_backstop_turn_closed', {});
          await closeTurnAndAck(turnEpoch, res.response || TURN_CLOSE_ACK);
          return;
        }
        // Clean, paced narration: speak the backend's markdown-free segments when
        // present (reuses SpeakableText + SegmentedNarrator); else the flat text.
        const segments = Array.isArray(res.narration_segments)
          ? res.narration_segments.filter((seg) => seg && typeof seg.text === 'string' && seg.text.trim() !== '')
          : [];
        if (isDomainChoice(res)) {
          // Aura asks which connected domain to analyse; the Copilot rail renders
          // the domains as selectable chips. Here we just speak the question.
          auraSpeechRef.current = res.reply;
          setAuraSections(null); // a domain-choice clarification has no answer sections
          retain(segments, res.reply);
          if (segments.length > 0) {
            await speakSegments(segments);
          } else {
            await speak(res.reply);
          }
          return;
        }
        // A real spoken answer was produced: this session has now been greeted
        // (a domain-choice clarification leaves it un-greeted so the eventual
        // answer still carries the greeting).
        hasGreetedRef.current = true;
        auraSpeechRef.current = res.display_text ?? res.proposal.text;
        // Render the answer in the SAME organised sectioned block the briefing
        // uses (fix for the dense-wall render); null => fall back to display text.
        setAuraSections(Array.isArray(res.sections) && res.sections.length > 0 ? res.sections : null);
        retain(segments, res.display_text ?? res.proposal.text);
        if (segments.length > 0) {
          await speakSegments(segments);
        } else {
          await speak(res.proposal.text);
        }
      } catch (err) {
        const ve = toVoiceError(err);
        diagError(ve.code, (err as Error)?.message ?? ve.message, (err as Error)?.stack);
        dispatch({ type: 'ERROR', error: ve });
      }
    },
    [engine, speak, speakSegments, greetingSignal, retain, closeTurnAndAck],
  );

  // ==== HANDS-FREE scoped-briefing CLARIFY flow (hands-free only) =============
  // (Endpoint 1 module-menu fetch removed by Aura brevity P2: a briefing ask no
  // longer asks "which module?" -- see handleBriefingWake.)

  // Endpoint 2: a briefing-intent assist scoped to module_scope (subset keys or
  // "all"). Same /ai/assist route + the ONE optional module_scope field; the reply
  // is the normal assist/briefing shape, just filtered to scope (sections + segments).
  const assistScoped = useCallback(
    async (utterance: string, moduleScope: string[] | 'all'): Promise<AssistProposalResponse> => {
      const sig = greetingSignal();
      const gctx: Record<string, string | number | boolean | null> = { modality: 'voice' };
      if (sig.localHour !== undefined) gctx.client_local_hour = sig.localHour;
      if (sig.tz !== undefined) gctx.client_tz = sig.tz;
      if (sig.greet) gctx.greet = true;
      return api.post<AssistProposalResponse>('/ai/assist', {
        prompt: utterance,
        engine: engine ?? null,
        subject_type: null,
        subject_id: null,
        context: gctx,
        module_scope: moduleScope,
      });
    },
    [engine, greetingSignal],
  );

  // Present an assist/briefing-shaped reply through the SAME sectioned render +
  // paced TTS path think() uses (so a scoped briefing sounds identical to that
  // module's block in a full briefing -> the TTS cache HITs).
  const presentAssistProposal = useCallback(
    async (res: AssistProposalResponse) => {
      const segments = Array.isArray(res.narration_segments)
        ? res.narration_segments.filter((seg) => seg && typeof seg.text === 'string' && seg.text.trim() !== '')
        : [];
      hasGreetedRef.current = true;
      const text = res.display_text ?? res.proposal?.text ?? '';
      auraSpeechRef.current = text;
      setAuraSections(Array.isArray(res.sections) && res.sections.length > 0 ? res.sections : null);
      retain(segments, text);
      if (segments.length > 0) await speakSegments(segments);
      else if (res.proposal?.text) await speak(res.proposal.text);
    },
    [retain, speakSegments, speak],
  );

  // Speak the closed-set "which module?" question (or a one-time re-ask). Barge-in
  // during it reuses the existing cancelNarration + epoch guards (via speak()).
  const speakClarifyQuestion = useCallback(
    async (modules: BriefingModule[], reask = false) => {
      const labels = modules.map((m) => m.label).filter((l) => typeof l === 'string' && l.trim() !== '');
      const list =
        labels.length <= 1
          ? labels.join('')
          : labels.slice(0, -1).join(', ') + ' or ' + labels[labels.length - 1];
      const q = reask
        ? 'Sorry — which would you like: ' + list + '? Or say all.'
        : 'Which would you like — ' + list + '? Or say all for the full briefing.';
      auraSpeechRef.current = q;
      setAuraSections(null);
      await speak(q);
    },
    [speak],
  );

  // FIRST hands-free command was briefing-intent. Aura brevity P2 (owner GO
  // 10-10-2026 16:47 UTC "go brevity": '"What's happening" behaves one way'): it
  // now takes the SAME path as a push-to-talk / follow-up "what's happening" --
  // the canonical server briefing via think() (short spoken highlights, the full
  // briefing on screen). The module-menu question ("Which would you like ...?")
  // is no longer asked for a briefing ask; that was the third behaviour the owner
  // heard on 10-10-2026 (14:18, 16:16).
  const handleBriefingWake = useCallback(
    async (utterance: string, turnEpoch: number) => {
      settleTurn('done'); // normal-turn settle, exactly like the 'normal' route
      void think(utterance, turnEpoch);
    },
    [think, settleTurn],
  );

  // FIRST hands-free command was a MODULE-SCOPED overview ask ("tell me about
  // SEO"): route DIRECTLY to the scoped briefing (assistScoped with the derived
  // module key), SKIPPING the "which module?" clarify menu entirely. The keys are
  // the same canonical menu keys (crm|seo|social|marketing) the BE menu validates,
  // so the server's own moduleScopedAsk agrees and the guard cap still applies.
  const handleModuleAskWake = useCallback(
    async (utterance: string, moduleScope: string[], turnEpoch: number) => {
      dispatch({ type: 'THINKING_STARTED' });
      try {
        const res = await assistScoped(utterance, moduleScope);
        if (isStale(turnEpoch, epochRef.current)) { settleTurn('done'); return; }
        settleTurn('done');
        void presentAssistProposal(res);
      } catch (err) {
        settleTurn('done');
        dispatch({ type: 'ERROR', error: toVoiceError(err) });
      }
    },
    [assistScoped, presentAssistProposal, settleTurn],
  );

  // TURN-CLOSE (owner #2.2 P1, 2026-07-25): a negative-intent answer on a
  // hands-free turn ("no" / "that's all" / "shut it") CLOSES the turn — short
  // spoken ack, settle, NO assist call, NO re-prompt. The machine returns to
  // armed (wake-word only) via the normal STATUS idle path when the ack ends,
  // so listening stops cleanly until the next explicit "Hey Aura".
  const handleTurnCloseWake = useCallback(
    async (turnEpoch: number) => {
      // D4 FIX (voice-wave-4, owner re-test-2 2026-07-27), CONSOLIDATED (Phase
      // B, voice-turn-audio-redesign 2026-07-27) -- turn CLOSING resets every
      // turn-session field a turn end owns, in ONE place: hasWoken -> false
      // (next wake greets fresh), closedAtMs -> now (starts the post-close
      // cooldown, see beginWakeTurn isInCooldown()), handsFreeTurn -> false,
      // subState -> null (was three separate refs: handsFreeTurnRef /
      // clarifyCtxRef / publishConfirmCtxRef). Residual #1 (2026-07-28): this
      // now runs through the SAME closeTurnAndAck the BE turn-close backstop
      // path in think() uses -- one choke point, not two independently
      // maintained close sequences.
      await closeTurnAndAck(turnEpoch, TURN_CLOSE_ACK);
    },
    [closeTurnAndAck],
  );

  // The spoken answer to the "which module?" question: match against the OFFERED
  // menu (closed set, vocab-from-response), then scope / all / re-ask-once / full.
  const handleClarifyAnswer = useCallback(
    async (answer: string, turnEpoch: number) => {
      const ctx = turnRef.current.subState;
      if (!ctx || ctx.kind !== 'clarify') { settleTurn('done'); return; }
      const step = resolveClarifyAnswer(matchModuleAnswer(answer, ctx.modules), ctx.reAskCount);
      if (step.kind === 're-ask') {
        turnRef.current.incReask();
        await speakClarifyQuestion(ctx.modules, true);
        if (isStale(turnEpoch, epochRef.current)) { turnRef.current.clearSubState(); settleTurn('done'); return; }
        settleTurn('clarify-capture');
        return;
      }
      turnRef.current.clearSubState();
      if (step.kind === 'scope') {
        try {
          const res = await assistScoped(ctx.utterance, step.keys);
          if (isStale(turnEpoch, epochRef.current)) { settleTurn('done'); return; }
          settleTurn('done');
          void presentAssistProposal(res);
        } catch (err) {
          settleTurn('done');
          dispatch({ type: 'ERROR', error: toVoiceError(err) });
        }
        return;
      }
      // 'all' or 'full' (unrecognised after the re-ask) -> the full briefing.
      settleTurn('done');
      void briefMe();
    },
    [assistScoped, presentAssistProposal, speakClarifyQuestion, briefMe, settleTurn],
  );

  // The spoken answer to "shall I publish this draft?": match the CLOSED SET
  // (yes/edit/no; close via the SHARED isTurnClose), then approve / edit / cancel /
  // re-ask-once-then-cancel. SAFETY: ambiguity NEVER publishes (resolver cancels).
  const handlePublishConfirmAnswer = useCallback(
    async (answer: string, turnEpoch: number): Promise<void> => {
      const ctx = turnRef.current.subState;
      if (!ctx || ctx.kind !== 'publish_confirm') return;
      const step = resolvePublishConfirmAnswer(matchPublishConfirmAnswer(answer), ctx.reAskCount);
      if (step.kind === 're-ask') {
        turnRef.current.incReask();
        void askPublishConfirmRef.current?.(true);
        return;
      }
      turnRef.current.clearSubState();
      if (isStale(turnEpoch, epochRef.current)) return;
      if (step.kind === 'approve') {
        // Park the proposal in the EXISTING owner-gated approval primitive. The
        // existing Approve/"Not now" card ("Nothing runs until you approve.") is THE
        // gate; NO publish/connector leg fires here (scope #3 — draft-only, forbidden).
        dispatch({ type: 'REQUEST_APPROVAL', action: buildPublishConfirmAction(ctx.draft) });
        auraSpeechRef.current = PUBLISH_CONFIRM_QUEUED_ACK;
        setAuraSections(null);
        try { await speak(PUBLISH_CONFIRM_QUEUED_ACK); } catch { /* ack best-effort */ }
        return;
      }
      if (step.kind === 'edit') {
        auraSpeechRef.current = PUBLISH_CONFIRM_EDIT_ACK;
        setAuraSections(null);
        try { await speak(PUBLISH_CONFIRM_EDIT_ACK); } catch { /* ack best-effort */ }
        options.onOpenDraftEditor?.(ctx.draft); // open the EXISTING editor surface
        return;
      }
      // cancel: leave it a draft, close the loop cleanly (nothing published).
      auraSpeechRef.current = PUBLISH_CONFIRM_CANCEL_ACK;
      setAuraSections(null);
      try { await speak(PUBLISH_CONFIRM_CANCEL_ACK); } catch { /* ack best-effort */ }
    },
    [speak, options],
  );

  // Start capturing an utterance (tap-to-talk down). Real providers record the
  // mic MediaStream; the mock needs no mic (demoSay drives it).
  // Aura publish P1: the batch Draft/Publish flow. onFinal / submitText route into
  // it; startListening is reached through a ref (it is defined just below).
  const batchListenRef = useRef<(() => Promise<unknown>) | null>(null);
  // C6b (owner decision 10-10-2026): the whole batch flow is owner/operator only.
  const auth = useOptionalAuth();
  const roleRef = useRef<string | undefined>(undefined);
  roleRef.current = auth?.activeWorkspace?.role;
  const canManagePublishingOpt = options.canManagePublishing;
  // The viewed domain, read at ask time through a ref (no re-render coupling).
  const viewedDomain = useActiveDomain().active;
  const activeDomainRef = useRef<BatchActiveDomain | null>(null);
  activeDomainRef.current = viewedDomain ? { projectId: viewedDomain.projectId, hostname: viewedDomain.hostname } : null;
  const activeDomainOpt = options.activeDomain;
  const batch = useBatchPublishVoice({
    deps: options.batchPublishDeps ?? defaultBatchPublishDeps,
    speak,
    turnRef,
    listenRef: batchListenRef,
    canManagePublishing: () => (canManagePublishingOpt ? canManagePublishingOpt() : canManagePublishing(roleRef.current)),
    activeDomain: () => (activeDomainOpt ? activeDomainOpt() : activeDomainRef.current),
  });
  const batchHandleAnswer = batch.handleAnswer;
  const batchDropQueuedWrites = batch.dropQueuedWrites;
  const batchBegin = batch.begin;
  // Aura P2: suggest + write 3 articles. On success the drafts are handed to the P1
  // batch flow above (Draft / Publish cards + its own confirm rules).
  const suggestListenRef = useRef<(() => Promise<unknown>) | null>(null);
  const suggest = useSuggestWriteVoice({
    deps: options.suggestWriteDeps ?? defaultSuggestWriteDeps,
    speak,
    turnRef,
    listenRef: suggestListenRef,
    activeDomain: () => (activeDomainOpt ? activeDomainOpt() : activeDomainRef.current),
    // Owner GO 12-10-2026: writing is owner/operator only (same role source as C6b).
    canWrite: () => (canManagePublishingOpt ? canManagePublishingOpt() : canManagePublishing(roleRef.current)),
    onWritten: (site, ids, channel) => batch.presentDrafts(site, ids, channel),
  });
  const suggestHandleAnswer = suggest.handleAnswer;
  // conversational-actions (owner GO 12-10-2026 20:30 UTC): "what do you recommend?" /
  // "show my pending blogs" -> one numbered list; picks go to the batch / suggest flows above.
  const recommendListenRef = useRef<(() => Promise<unknown>) | null>(null);
  const canManageNow = (): boolean => (canManagePublishingOpt ? canManagePublishingOpt() : canManagePublishing(roleRef.current));
  const recommend = useRecommendVoice({
    batchDeps: options.batchPublishDeps ?? defaultBatchPublishDeps,
    suggestDeps: options.suggestWriteDeps ?? defaultSuggestWriteDeps,
    speak,
    turnRef,
    listenRef: recommendListenRef,
    activeDomain: () => (activeDomainOpt ? activeDomainOpt() : activeDomainRef.current),
    canManage: canManageNow,
    batch: { begin: batch.begin, presentDrafts: batch.presentDrafts },
    suggest: { begin: suggest.begin, adoptAndWrite: suggest.adoptAndWrite },
  });
  const recommendBegin = recommend.begin;
  const recommendHandleAnswer = recommend.handleAnswer;
  // every write ask goes through beginWrite: a queued (already written) draft gets the
  // publish offer; anything else is the unchanged suggest/write flow.
  const suggestBegin = recommend.beginWrite;

  const startListening = useCallback(async (): Promise<{ ok: boolean; firstPermissionGrant: boolean }> => {
    const turnEpoch = epochRef.current; // the turn this capture belongs to
    const grantedBefore = micEverGrantedRef.current;
    let usedRealMic = false;
    try {
      let mediaStream: MediaStream | undefined;
      if (!options.sttAdapter && config.stt === 'mock') {
        // Mock path: no real mic needed.
      } else if (sharedMicRef.current) {
        // HANDS-FREE: REUSE the wake engine's mic (no 2nd getUserMedia). We do
        // NOT own it (micRef stays null) so finish/stop never stop the wake mic.
        mediaStream = sharedMicRef.current;
      } else if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        // REAL mic: on a genuine FIRST use this shows the permission prompt and
        // AWAITS the user's decision. That await can outlive the press gesture —
        // on mobile a pointerup/pointercancel fires while the prompt is open.
        micRef.current = await navigator.mediaDevices.getUserMedia(RAW_AUDIO_CONSTRAINTS);
        mediaStream = micRef.current;
        usedRealMic = true;
        micEverGrantedRef.current = true;
      } else {
        dispatch({ type: 'ERROR', error: { code: 'mic_unavailable', message: 'No microphone is available.' } });
        return { ok: false, firstPermissionGrant: false };
      }
      // Cancelled/stopped DURING startup (a pointercancel or Stop bumped the epoch
      // while the permission prompt / mic-open was awaiting): DISCARD — never enter
      // 'listening' behind the user's back, and never leak the just-opened mic.
      if (isStale(turnEpoch, epochRef.current)) {
        micRef.current?.getTracks().forEach((t) => t.stop());
        micRef.current = null;
        return { ok: false, firstPermissionGrant: false };
      }
      const stream = await stt.openStream(
        { locale: persona.accent, mediaStream },
        {
          onInterim: (t) => dispatch({ type: 'STT_INTERIM', text: t }),
          onFinal: (t) => {
            // A late STT result after Stop / a new turn must be ignored (stop-stuck fix).
            // IVG #2.3-F1 belt+braces: settle the (possibly armed) wake-turn
            // outcome before bailing so endWakeCommand can never hang on it.
            // Epoch/staleness stays HERE (an IO-timing concern owned by the
            // glue, not turn-content) — Phase B's per-state routing begins
            // only once we know this result is not stale.
            if (isStale(turnEpoch, epochRef.current)) { settleTurn('done'); return; }
            // F2 (r2 owner device test 10-10-2026 21:23 UTC): the request capture
            // loses the words spoken right after "Hey Aura" ("push the next draft
            // to booksesh.com" arrived as "To booksearch.com"); the server confirm
            // clip heard them. Merge its after-wake words into THIS wake turn's
            // first final, once, before any routing sees the transcript.
            // Fable C-AO1: consumed by ANY final; merged ONLY into a NON-EMPTY final
            // of the SAME wake turn (epoch match) -- never into a later PTT/typed
            // turn, and never acted on alone: a capture that heard no speech closes
            // the same way with diag ON (empty STT final) and OFF (local discard).
            const remainder = wakeRemainderRef.current;
            wakeRemainderRef.current = null;
            if (remainder !== null && remainder.epoch === turnEpoch && t.trim() !== '') {
              const merged = mergeWakeRemainder(remainder.text, t);
              if (merged !== t.trim()) voiceBeacon('wake_remainder_merged', { capture_len: t.trim().length, after_wake_len: remainder.text.length, merged_len: merged.length });
              t = merged;
            } else if (remainder !== null) {
              voiceBeacon('wake_remainder_dropped', { reason: remainder.epoch !== turnEpoch ? 'other_turn' : 'empty_capture' });
            }
            // F9: drop leaked greeting/wake fragments ("Help. Hey. What's happening
            // today?" -> "What's happening today?") before routing.
            {
              const cleaned = stripWakeEcho(t);
              if (cleaned !== t.trim()) { voiceBeacon('wake_echo_stripped', { before_len: t.trim().length, after_len: cleaned.length }); t = cleaned; }
            }
            // Phase B (voice-turn-audio-redesign, 2026-07-27): the ordering-
            // sensitive if-chain that used to live inline here (publish-confirm
            // precedence, turn-close-before-fragment, clarify-answer-before-
            // fragment, clarify no-answer fallback, hands-free module-ask/
            // briefing routing) is now ONE pure, unit-tested dispatch on the
            // turn entity — see wake/turnEntity.ts ConversationTurn.classifyFinal.
            // Every branch below is BYTE-IDENTICAL side-effect ordering to the
            // pre-Phase-B chain (see the Phase B build note's preservation table).
            const route = turnRef.current.classifyFinal(t);
            // Fable IVG C2: anything but the answer to the open publish read-back (a new ask, a
            // turn close, a quiet close) drops the writes queued behind that read-back.
            if (route.kind !== 'batch_publish_answer') batchDropQueuedWrites();
            switch (route.kind) {
              case 'publish_confirm_answer':
                // IVG #2.3-DF3: settle the (possibly armed) wake-turn outcome —
                // the confirm loop re-opens capture via askPublishConfirmRef,
                // never via the wake outcome, so endWakeCommand must not keep
                // awaiting.
                settleTurn('done');
                dispatch({ type: 'STT_FINAL', text: t });
                void handlePublishConfirmAnswer(t.trim(), turnEpoch);
                return;
              case 'batch_publish_answer':
                // Aura publish P1: the closed-set "publish" / "draft" answer while the
                // batch cards are on screen (bare yes -> "Publish or save as draft?").
                settleTurn('done');
                dispatch({ type: 'STT_FINAL', text: t });
                void batchHandleAnswer(t.trim(), 'voice');
                return;
              case 'suggest_write_answer':
                // Aura P2: the closed-set "yes" / "write them" / "no" answer while the
                // suggestion cards are on screen.
                settleTurn('done');
                dispatch({ type: 'STT_FINAL', text: t });
                void suggestHandleAnswer(t.trim(), 'voice');
                return;
              case 'suggest_write_ask':
                // Aura P2: "which 3 articles should we write?" -> cards + one-line summary.
                dispatch({ type: 'STT_FINAL', text: t });
                settleTurn('done');
                void suggestBegin(route.ask, 'voice');
                return;
              case 'recommend_answer':
                // conversational-actions: "2" / "1 and 3" / "the kambo one" / "all" / "no"
                // answering the open recommend question.
                settleTurn('done');
                dispatch({ type: 'STT_FINAL', text: t });
                void recommendHandleAnswer(t.trim(), 'voice');
                return;
              case 'recommend_ask':
                dispatch({ type: 'STT_FINAL', text: t });
                settleTurn('done');
                // A role without publishing access still gets the READ-ONLY pending list
                // from the server (unchanged); the actionable list is owner/operator only.
                if (route.ask.source === 'pending' && !canManageNow()) { void think(t.trim(), turnEpoch); return; }
                void recommendBegin(route.ask, 'voice');
                return;
              case 'batch_publish_ask':
                // Aura publish P1: "push the next N drafts (to <site>)" -> cards + question.
                dispatch({ type: 'STT_FINAL', text: t });
                settleTurn('done');
                void batchBegin(route.ask, 'voice');
                return;
              case 'turn_close':
                // STT_FINAL first: it moves the machine out of 'listening' so
                // the close ACK renders as 'speaking' (SPEAK is only valid from
                // thinking/idle) and capture stops — mirroring the publish-
                // confirm path above. No think()/brain call: handleTurnCloseWake
                // only settles the wake outcome + speaks the ack.
                dispatch({ type: 'STT_FINAL', text: t });
                void handleTurnCloseWake(turnEpoch);
                return;
              case 'clarify_answer':
                dispatch({ type: 'STT_FINAL', text: t });
                void handleClarifyAnswer(t.trim(), turnEpoch);
                return;
              case 'clarify_no_answer_fallback':
                // Empty/no-answer DURING a clarify must not exit silently —
                // fall back to the FULL briefing (hands-free no-answer policy).
                turnRef.current.clearSubState();
                settleTurn('done');
                void briefMe();
                return;
              case 'fragment_reprompt':
                // IVG #2.3-F2 (same deadlock family): an empty-transcript
                // reprompt ENDS any in-flight wake turn — settle so
                // endWakeCommand never awaits a promise this branch abandoned.
                // Idempotent no-op on PTT. voice residual #2: settle 'empty' (not
                // 'done') — EMPTY_TRANSCRIPT_REPROMPT.code is literally 'stt_failed'
                // with no content, exactly the empty-capture refractory's signal.
                settleTurn('empty');
                dispatch({ type: 'ERROR', error: EMPTY_TRANSCRIPT_REPROMPT });
                return;
              case 'quiet_close':
                // F3 follow-up cap (10-10-2026): a capped (silent) follow-up wake
                // heard no real request -> close the turn QUIETLY: no ack, no
                // reprompt, no brain call. close() resets hasWoken + the cap and
                // starts the post-close cooldown; STT_FINAL('') returns the
                // machine to idle (armed, wake-word only).
                voiceBeacon('followup_cap_quiet_close', {});
                settleTurn('empty');
                turnRef.current.close(Date.now());
                dispatch({ type: 'STT_FINAL', text: '' });
                return;
              case 'module_ask':
                dispatch({ type: 'STT_FINAL', text: t });
                void handleModuleAskWake(t.trim(), route.keys, turnEpoch);
                return;
              case 'briefing':
                dispatch({ type: 'STT_FINAL', text: t });
                void handleBriefingWake(t.trim(), turnEpoch);
                return;
              case 'normal':
                dispatch({ type: 'STT_FINAL', text: t });
                // Normal turn: settle immediately so endWakeCommand does not
                // await think().
                settleTurn('done');
                void think(t.trim(), turnEpoch);
                return;
            }
          },
          onError: (e) => {
            // IVG #2.3-DF4: an STT transport failure mid-wake-turn must settle
            // the armed outcome (same deadlock family) before surfacing the error.
            // voice residual #2: settle 'empty' (not 'done') — an STT transport
            // failure produced no content, the same empty-capture class as a
            // fragment reprompt.
            settleTurn('empty');
            dispatch({ type: 'ERROR', error: toVoiceError(e) });
          },
          // GATED FE diagnostics: beacon the capture metadata + the STT action
          // (transcribed / low-confidence drop / empty). Correlates by cid with
          // the backend stt.receive/stt.result lines so the owner's Android
          // retest pinpoints STT accuracy + silent low-confidence drops. No
          // transcript TEXT is sent — length + confidence + sizes only.
          onSttMeta: (m) => diagSttResult(m),
        },
      );
      // openStream can itself await (socket/worklet) — re-check staleness so a
      // cancel during THAT window also discards cleanly instead of listening.
      if (isStale(turnEpoch, epochRef.current)) {
        void stream.close();
        micRef.current?.getTracks().forEach((t) => t.stop());
        micRef.current = null;
        return { ok: false, firstPermissionGrant: false };
      }
      streamRef.current = stream;
      // Capture is genuinely LIVE now — openStream() resolves ONLY after the
      // MediaRecorder has fired `start` (actually begun recording), not merely
      // when start() was called. So 'listening' / "speak now" begins only into a
      // live recorder — the user's LEADING words are no longer dropped during the
      // mobile mic warm-up / first-permission grant (leading-audio truncation fix
      // 2026-07-22). This is the ONLY point 'listening' begins.
      dispatch({ type: 'PRESS_START' });
      return { ok: true, firstPermissionGrant: usedRealMic && !grantedBefore };
    } catch {
      // Denied / hardware error. Surface a recoverable error WITH a clear retry
      // path (pressing Talk again clears it + re-requests) — never a dead end.
      dispatch({
        type: 'ERROR',
        error: {
          code: 'mic_permission_denied',
          message: 'Microphone access is blocked. Allow the mic for this site in your browser settings, then press Talk to try again.',
        },
      });
      return { ok: false, firstPermissionGrant: false };
    }
  }, [stt, persona.accent, think, options.sttAdapter, config.stt, handleClarifyAnswer, handleBriefingWake, handleModuleAskWake, handleTurnCloseWake, handlePublishConfirmAnswer, briefMe, settleTurn, batchHandleAnswer, batchBegin, suggestHandleAnswer, suggestBegin, recommendBegin, recommendHandleAnswer, batchDropQueuedWrites]); // eslint-disable-line react-hooks/exhaustive-deps
  batchListenRef.current = startListening;
  suggestListenRef.current = startListening;
  recommendListenRef.current = startListening;

  // ==== #7 POST-PREP PUBLISH-CONFIRM loop (voice turn-state; PTT + hands-free) ==
  // After a prepared draft card is shown, Aura ASKS "shall I publish this draft?"
  // and opens the answer capture DIRECTLY (never via the wake 'armed' phase),
  // reusing the SAME Listening/STT path as any turn. onFinal routes the closed-set
  // answer to handlePublishConfirmAnswer. askPublishConfirm is stored in a ref so
  // the (earlier-defined) re-ask leg can reopen capture without a forward dependency.
  const askPublishConfirm = useCallback(
    async (reask: boolean): Promise<void> => {
      const ctx = turnRef.current.subState;
      if (!ctx || ctx.kind !== 'publish_confirm') return;
      const line = reask ? PUBLISH_CONFIRM_REASK : PUBLISH_CONFIRM_QUESTION;
      auraSpeechRef.current = line;
      setAuraSections(null);
      const turnEpoch = epochRef.current;
      try { await speak(line); } catch { /* the question is best-effort */ }
      if (isStale(turnEpoch, epochRef.current)) { turnRef.current.clearSubState(); return; }
      await startListening(); // open the answer capture (onFinal -> handlePublishConfirmAnswer)
    },
    [speak, startListening],
  );
  askPublishConfirmRef.current = askPublishConfirm;

  const beginPublishConfirm = useCallback(
    async (draft: PublishConfirmDraft): Promise<void> => {
      nextEpoch(); // a fresh turn — invalidate any late async from a prior turn
      turnRef.current.setPublishConfirm(draft);
      await askPublishConfirm(false);
    },
    [askPublishConfirm, nextEpoch],
  );

  // Finish the utterance (tap-to-talk up): stop recording, transcribe via the
  // server proxy (onFinal -> think), then release the mic.
  const finishListening = useCallback(async () => {
    const s = streamRef.current;
    if (!s) return;
    await s.finish();
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    streamRef.current = null;
  }, []);

  // Push-to-talk (hold to record, release to send). A press-active guard makes
  // the pair idempotent so overlapping mouse+touch events or key-repeat cannot
  // double-start; releasePending covers a release that arrives before the mic
  // has finished opening.
  const pressActiveRef = useRef(false);
  const releasePendingRef = useRef(false);

  // PTT-COEXIST (owner #2.2 P3): observer the hands-free host registers so a
  // manual press PARKS the wake flow (instead of racing it). Best-effort seam —
  // null when hands-free is not mounted; PTT behaviour is otherwise unchanged.
  const pttObserverRef = useRef<(() => void) | null>(null);
  const setPttObserver = useCallback((cb: (() => void) | null) => { pttObserverRef.current = cb; }, []);

  const pressToTalk = useCallback(async () => {
    if (pressActiveRef.current) return;        // already holding
    if (state.status === 'thinking') return; // brain busy — ignore the hold
    pressActiveRef.current = true;
    try { pttObserverRef.current?.(); } catch { /* observer must never break PTT */ }
    // IVG F3 (2026-07-25) + IVG #7-F2: a manual press takes OWNERSHIP of the
    // turn — clear the wake-only turn-entity state (Phase B: ONE call replaces
    // the three separate ref clears) so PTT speech can never route into a
    // pending hands-free clarify/publish-confirm sub-state or inherit
    // hands-free routing.
    turnRef.current.resetForManualTurn();
    releasePendingRef.current = false;
    nextEpoch();            // new turn — invalidate any in-flight async result
    setAuraSections(null);  // don't let a prior answer's sections linger while listening
    // Barge-in: cancel any in-progress briefing narration queue + clear any
    // un-played (blocked) reply so a stale "tap to play" does not linger.
    cancelNarration('press_to_talk');
    player.stop();
    // CRITICAL: resume the shared AudioContext on THIS user gesture so Aura's
    // reply plays back across the async STT/AI/TTS gap without a second tap.
    // Close any capture still open from a prior turn before opening a new one
    // (prevents a leaked mic/STT stream if a press lands while one is still live).
    if (streamRef.current) { diagRecorderRestart('press_over_live_stream'); releaseMic(); }
    void player.unlock();
    diagAudioCtx(player.getState().unlocked, player.getState().needsUserPlay);
    const result = await startListening();
    if (!result.ok) {
      // Capture never came up (permission denied / unavailable / cancelled mid
      // start). The hold is over; any error already surfaced a retry path.
      pressActiveRef.current = false;
      releasePendingRef.current = false;
      return;
    }
    // Released DURING mic startup? Decide what that deferred release means now
    // that capture has actually resolved (first-permission race fix, 2026-07-22).
    if (releasePendingRef.current) {
      releasePendingRef.current = false;
      pressActiveRef.current = false;
      if (resolveDeferredRelease({ captureLive: true, firstPermissionGrant: result.firstPermissionGrant }) === 'send') {
        // Normal/subsequent use: a genuine quick hold-release -> send exactly once.
        dispatch({ type: 'PRESS_END' });
        await finishListening();
      }
      // else 'stay-listening': the FIRST permission prompt stole the press gesture.
      // Approval => stay in Listening (never phantom-send); the user speaks and the
      // listenGuard record-cap bounds the open capture. Subsequent uses are normal.
    }
  }, [state.status, startListening, finishListening, player, cancelNarration, nextEpoch, releaseMic]);

  const releaseToTalk = useCallback(async () => {
    if (!pressActiveRef.current) return;       // no active hold
    if (streamRef.current === null) {          // startup still in flight
      releasePendingRef.current = true;
      return;
    }
    pressActiveRef.current = false;
    dispatch({ type: 'PRESS_END' });
    // Re-prime on THIS release gesture too. On mobile, opening the mic
    // (getUserMedia) can interrupt/suspend the shared AudioContext, and iOS
    // refuses resume() outside a user gesture. Release is the last real gesture
    // before the STT/AI/TTS gap, and it fires AFTER the mic closes — resuming
    // here (plus the player's silent keep-alive) keeps the context "running" so
    // every reply segment can play without a second tap.
    void player.unlock();
    diagAudioCtx(player.getState().unlocked, player.getState().needsUserPlay);
    await finishListening();
  }, [finishListening, player]);

  // Abort an in-progress hold WITHOUT sending (touch/pointer-cancel). A cancelled
  // gesture (system back-swipe, scroll-steal) must DISCARD the capture, not
  // transcribe a partial utterance. Distinct from releaseToTalk (which SENDS):
  // it bumps the epoch (invalidating any in-flight async), closes the STT stream
  // (discards the buffer + aborts any upload) via releaseMic, releases the mic,
  // and returns to idle. The borrowed hands-free mic (micRef null) is left alone.
  const cancelToTalk = useCallback(() => {
    const hadHold = pressActiveRef.current || releasePendingRef.current || streamRef.current !== null;
    pressActiveRef.current = false;
    releasePendingRef.current = false;
    if (!hadHold) return;
    nextEpoch();            // invalidate any in-flight STT/think for this turn
    cancelNarration('cancel_to_talk');
    player.stop();
    releaseMic();           // close()s the STT stream (discard + abort) + stops owned mic tracks
    turnRef.current.clearSubStateIfKind('publish_confirm'); // IVG #7-F2: cancelled gesture drops the pending confirm (clarify sub-state, if any, is left untouched here — unchanged pre-Phase-B behaviour)
    turnRef.current.clearSubStateIfKind('batch_publish'); // Aura publish P1: a cancelled gesture drops the spoken confirm (the cards stay tappable)
    turnRef.current.clearSubStateIfKind('suggest_write'); // Aura P2: same — the suggestion cards stay tappable
    turnRef.current.clearSubStateIfKind('recommend'); // conversational-actions: the list stays tappable
    batchDropQueuedWrites(); // Fable IVG C2: a cancelled gesture drops writes queued behind a read-back
    dispatch({ type: 'STOP' });
  }, [releaseMic, player, cancelNarration, nextEpoch, batchDropQueuedWrites]);

  const submitText = useCallback(
    async (text: string) => {
      const utterance = text.trim();
      if (utterance === '') return;
      const turnEpoch = nextEpoch();  // new turn
      setAuraSections(null);
      // Text submit is also a user gesture — prime playback so the spoken reply
      // is not autoplay-blocked; and barge over any in-progress briefing.
      cancelNarration('submit_text');
      void player.unlock();
      dispatch({ type: 'SUBMIT_TEXT', text: utterance });
      // Aura publish P1 (typed chat in the Aura popup): a typed "publish"/"draft"
      // answers the open batch question; a typed "push the next N drafts" starts it.
      if (turnRef.current.subState?.kind === 'batch_publish') {
        await batchHandleAnswer(utterance, 'chat');
        return;
      }
      batchDropQueuedWrites(); // Fable IVG C2: a new typed ask drops queued writes
      // Aura P2 (typed chat in the Aura popup): a typed "yes"/"no" answers the open
      // "Shall I write them?"; a typed "which 3 articles should we write?" starts it.
      if (turnRef.current.subState?.kind === 'suggest_write') {
        await suggestHandleAnswer(utterance, 'chat');
        return;
      }
      if (turnRef.current.subState?.kind === 'recommend') {
        await recommendHandleAnswer(utterance, 'chat');
        return;
      }
      const typedSuggestAsk = detectSuggestWriteAsk(utterance);
      if (typedSuggestAsk) {
        await suggestBegin(typedSuggestAsk, 'chat');
        return;
      }
      const typedBatchAsk = detectBatchPublishAsk(utterance);
      if (typedBatchAsk) {
        await batchBegin(typedBatchAsk, 'chat');
        return;
      }
      const typedRecAsk = detectRecommendAsk(utterance) ?? detectPendingListAsk(utterance);
      if (typedRecAsk && !(typedRecAsk.source === 'pending' && !canManageNow())) {
        await recommendBegin(typedRecAsk, 'chat');
        return;
      }
      await think(utterance, turnEpoch);
    },
    [think, player, cancelNarration, nextEpoch, batchHandleAnswer, batchBegin, suggestHandleAnswer, suggestBegin, recommendBegin, recommendHandleAnswer, batchDropQueuedWrites], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const replay = useCallback(async () => {
    // User tapped "tap to play" — a real gesture. Bind "speaking" to the real
    // start and release it on end, exactly like a normal spoken reply.
    const outcome = await player.replay({ onStart: () => dispatch({ type: 'SPEAK' }) });
    if (outcome === 'played') {
      await waitForPlaybackEnd();
      dispatch({ type: 'SPEAKING_ENDED' });
    }
  }, [player, waitForPlaybackEnd]);

  // Replay the retained LAST response through the EXISTING paced path. Pure
  // client re-playback: with the default-on TTS cache the identical text is
  // re-spoken for free (NO new /v1/ai assist/briefing call), and onSpeakStart
  // re-enters 'speaking' (SPEAK is valid from idle) so the Speaking visual shows.
  // Offered only when idle with a retained response; muted => silent (the
  // transcript stays on screen). A new hold / Stop interrupts it via the SAME
  // barge-in that cancels any narration (speakSegments starts a fresh narrator).
  const replayLast = useCallback(async () => {
    const plan = replayPlan(stateRef.current.status, stateRef.current.muted, retainedRef.current);
    if (plan !== 'play') return;
    void player.unlock(); // a real user-gesture click — keep the audio context running
    // Replay re-runs the retained segments through the SAME paced narrator so a
    // multi-segment reply breathes on replay exactly as it did live; the default-on
    // TTS cache re-serves each segment for FREE (keyed per text+voice+speed) — NO
    // new /v1/ai assist/briefing call, NO STT, NO duplicate provider billing. Only
    // a SINGLE-segment reply may take the buffered-clip fast path (no pacing to
    // reproduce): re-play the exact bytes already in hand for an instant replay.
    const retainedNow = retainedRef.current!;
    const buf = lastAudioRef.current;
    if (retainedNow.segments.length <= 1 && buf && buf.bytes.byteLength > 0) {
      narratorRef.current?.cancel();
      player.stop();
      const outcome = await player.playBytes(buf.bytes, buf.mime, { onStart: () => dispatch({ type: 'SPEAK' }) });
      if (outcome === 'played') {
        await waitForPlaybackEnd();
        dispatch({ type: 'SPEAKING_ENDED' });
      }
      return;
    }
    await speakSegments(retainedNow.segments);
  }, [player, speakSegments, waitForPlaybackEnd]);

  const stop = useCallback(() => {
    wakeRemainderRef.current = null; // Fable C-AO1
    pressActiveRef.current = false;
    releasePendingRef.current = false;
    // IVG #2.3-F1: a Stop during the settle window (VAD endpoint -> onFinal)
    // must RESOLVE any armed turn outcome, or endWakeCommand awaits forever
    // (turnInFlightRef stuck -> STATUS suppressed -> wake parked permanently).
    // Resolving is idempotent (no-op when none armed / already settled).
    settleTurn('done');
    nextEpoch();  // invalidate any in-flight STT/think/speak so nothing can restart Aura
    cancelNarration('stop');
    player.stop();
    releaseMic();
    turnRef.current.clearSubStateIfKind('publish_confirm'); // IVG #7-F2: Stop drops any pending publish-confirm (clarify sub-state, if any, is left untouched here — unchanged pre-Phase-B behaviour)
    turnRef.current.clearSubStateIfKind('batch_publish'); // Aura publish P1: Stop drops the spoken confirm (the cards stay tappable)
    turnRef.current.clearSubStateIfKind('suggest_write'); // Aura P2: Stop drops the spoken confirm (the cards stay tappable)
    turnRef.current.clearSubStateIfKind('recommend'); // conversational-actions: Stop drops the open question (the list stays tappable)
    batchDropQueuedWrites(); // Fable IVG C2: Stop drops writes queued behind a read-back
    dispatch({ type: 'STOP' });
  }, [releaseMic, player, cancelNarration, nextEpoch, settleTurn, batchDropQueuedWrites]);

  const interrupt = useCallback(() => {
    nextEpoch();            // barge-in is a new turn — invalidate in-flight results
    setAuraSections(null);
    cancelNarration('interrupt');
    player.stop();
    dispatch({ type: 'INTERRUPT' });
    // Barge-in ALWAYS (re)starts listening — never the finish branch.
    void player.unlock();
    diagRecorderRestart('barge_in');
    void startListening();
  }, [startListening, player, cancelNarration, nextEpoch]);

  // HANDS-FREE greeting: play the pre-generated cached prompt through the SAME
  // unlocked AuraAudioPlayer (gesture-unlock + keep-alive) so it sounds without a
  // fresh gesture. Fetched once (a same-origin STATIC asset — NOT a provider call
  // and only AFTER a local wake has fired, so zero pre-wake provider egress).
  // Synthesize a short prompt to audio bytes via the EXISTING TTS adapter (the
  // same synth path speak() uses), WITHOUT touching the speaking-state machine —
  // so the spoken FOLLOW-UP wake prompt behaves exactly like the greeting asset.
  const synthesizeToBytes = useCallback(async (text: string): Promise<Uint8Array | null> => {
    const synth = tts.synthesize(text, {
      voice: config.ttsVoiceId,
      locale: persona.accent,
      provider: config.tts,
      providerVersion: 'beta',
    });
    const chunks: Uint8Array[] = [];
    try {
      for await (const chunk of synth.stream()) {
        if (chunk.bytes && chunk.bytes.byteLength > 0) chunks.push(chunk.bytes);
      }
    } catch (err) {
      // SILENT-CATCH AUDIT (voice-wave-4): the caller (playWakeGreeting) already
      // degrades safely on a null return (proceeds straight to listening with no
      // spoken prompt) -- but that degrade was previously untraceable.
      voiceBeacon('wake_prompt_synth_failed', { message: (err as Error)?.message ?? 'unknown' });
      return null;
    }
    return chunks.length > 0 ? concatChunks(chunks) : null;
  }, [tts, config.ttsVoiceId, config.tts, persona.accent]);

  const playWakeGreeting = useCallback(async (): Promise<WakePrompt['kind']> => {
    void player.unlock();
    // FIRST wake of the session -> the cached "How can I help?" greeting asset;
    // every SUBSEQUENT wake -> speak "Is there anything else?" via the EXISTING
    // TTS path (owner FIX 2, 2026-07-25 — NO new pre-recorded asset).
    const prompt = selectWakePrompt(turnRef.current.hasWoken, turnRef.current.followUpPromptsSpoken);
    if (prompt.kind === 'silent') {
      // F3 follow-up cap (10-10-2026): the one "anything else?" for this
      // conversation was already spoken -- open the capture WITHOUT re-prompting.
      voiceBeacon('wake_followup_capped', { spoken: turnRef.current.followUpPromptsSpoken });
      return 'silent';
    }
    if (prompt.kind === 'tts') {
      // FIX 2 EDGE (b): synthesise the follow-up prompt ONCE, then cache + replay
      // the bytes on every subsequent follow-up (mirrors the greeting-mp3 cache).
      if (!followUpAudioRef.current) {
        const synthesised = await synthesizeToBytes(prompt.text);
        if (synthesised && synthesised.byteLength > 0) followUpAudioRef.current = synthesised;
      }
      const bytes = followUpAudioRef.current;
      if (!bytes || bytes.byteLength === 0) return 'tts'; // synth failed -> proceed to listen
      // Mirrors the greeting: play through the unlocked player WITHOUT dispatching
      // SPEAK, so the wake phase stays idle then opens the capture.
      const outcome = await player.playBytes(bytes, mimeForProvider, {});
      if (outcome === 'played') {
        turnRef.current.noteFollowUpPromptPlayed(); // F3: count only a prompt that really played
        await waitForPlaybackEnd();
      }
      return 'tts';
    }
    // OPTION A (owner decision 10-10-2026 "A: Chime, not speech (Recommended)",
    // docs/brain/governance/owner-decision-aura-publish-choice-2026-10-10.md):
    // the first-wake cue ('greeting' prompt kind) is a short GENERATED chime,
    // never the spoken "How can I help?" asset -- false wakes score as high as
    // real ones, so a false wake must cost the owner a soft chime, not speech.
    // No fetch, no provider call; same player (same output volume); skipped
    // when Aura's speaker is muted. The turn it opens is QUIET (beginWakeTurn):
    // no intelligible speech -> silent close (diag ON via quiet_close, diag OFF
    // via noSpeechDiscard -- the C2 parity).
    if (stateRef.current.muted) {
      voiceBeacon('wake_chime', { played: false, reason: 'muted' });
      return 'greeting';
    }
    // NOTE: chime playback does NOT dispatch SPEAK — the machine stays idle
    // during it (the hands-free hook is in its own wake phase).
    const outcome = await player.playBytes(wakeChimeBytes(), WAKE_CHIME_MIME, {});
    voiceBeacon('wake_chime', { played: outcome === 'played' });
    if (outcome === 'played') {
      // Bounded: the chime is ~170 ms; never let a missing 'ended' event (a
      // device/output quirk) hold the wake turn -- the capture opens after at
      // most WAKE_CHIME_MAX_WAIT_MS either way.
      let chimeTimer: ReturnType<typeof setTimeout> | null = null;
      const ended = await Promise.race([
        waitForPlaybackEnd().then(() => true),
        new Promise<boolean>((r) => { chimeTimer = setTimeout(() => r(false), WAKE_CHIME_MAX_WAIT_MS); }),
      ]);
      if (chimeTimer) clearTimeout(chimeTimer);
      if (!ended) voiceBeacon('wake_chime_end_timeout', { ms: WAKE_CHIME_MAX_WAIT_MS });
      // FIX 2 EDGE (a), unchanged: mark the session "woken" ONLY after the first
      // cue has ACTUALLY played, so the NEXT wake in this conversation gets the
      // one "Is there anything else?" (F3, unchanged) and a blocked cue retries.
      turnRef.current.markWoken();
    }
    return 'greeting';
  }, [player, waitForPlaybackEnd, synthesizeToBytes, mimeForProvider]);

  // HANDS-FREE onWake: greet, then enter the EXISTING Listening/STT capture on the
  // shared wake mic (reuses startListening -> the same STT/assist/TTS pipeline).
  const beginWakeTurn = useCallback(async () => {
    // D4 FIX (voice-wave-4, owner re-test-2 2026-07-27): POST-CLOSE COOLDOWN.
    // A wake fire inside POST_CLOSE_COOLDOWN_MS of the last explicit turn close
    // is very likely the SAME marginal re-fire class the owner reproduced (a
    // near-threshold score seconds after "no that's all thanks", echo/AGC/NS
    // all off) -- not a genuine fresh "Hey Aura". IGNORE it here, BEFORE the
    // greeting ever plays, so it never speaks the "anything else?" follow-up
    // (or any prompt) and never opens a capture. Throwing surfaces to the wake
    // bridge's existing beginWakeTurn.catch -> WAKE_FAILED -> back to 'armed' --
    // the SAME explicit-transition path a real begin-turn failure already uses,
    // never a new silent state. A GENUINE wake after the window is unaffected
    // and plays the FRESH greeting (hasWoken was reset at close).
    if (turnRef.current.isInCooldown(Date.now())) {
      const since = turnRef.current.closedAtMs === null ? 0 : Date.now() - turnRef.current.closedAtMs;
      voiceBeacon('post_close_cooldown_suppressed', { since_ms: since });
      throw new Error('post_close_cooldown');
    }
    // IVG F8 (2026-07-25): a PTT press during the greeting bumps the epoch
    // (pressToTalk -> nextEpoch). If that happened while we awaited the
    // greeting, the USER'S manual turn owns the pipeline — ABORT this wake
    // turn instead of stealing its epoch/routing and double-starting capture.
    // (The machine has already parked via PTT_START; GREETED is a no-op there.)
    const greetEpoch = epochRef.current;
    const promptKind = await playWakeGreeting();
    if (isStale(greetEpoch, epochRef.current)) return;
    nextEpoch();            // a fresh turn — invalidate any stale async result
    // OPTION A (owner 10-10-2026): a chime wake ('greeting' kind) is QUIET too --
    // nothing intelligible after the chime closes silently, never "didn't catch that".
    turnRef.current.startWakeTurn(promptKind === 'silent' || promptKind === 'greeting'); // F3: a capped (silent) wake is a quiet turn; enables the briefing-intent trigger for THIS turn; IVG #2.3-DF3: a fresh wake turn never inherits a stale clarify/publish-confirm sub-state
    armTurnOutcome();       // endWakeCommand will await this turn's DECISION
    setAuraSections(null);
    cancelNarration('wake_turn');
    void player.unlock();
    await startListening(); // PRESS_START -> listening; onFinal -> think/clarify (shared mic)
  }, [playWakeGreeting, startListening, player, cancelNarration, nextEpoch, armTurnOutcome]);

  // WAKE CASCADE P1 (owner GO "go phase 1", 10-10-2026; docs/brain/governance/
  // owner-decision-aura-publish-choice-2026-10-10.md): a local fire opens the
  // capture SILENTLY. Nothing is audible until the server confirms "Aura".
  const beginSilentWakeTurn = useCallback(async () => {
    if (turnRef.current.isInCooldown(Date.now())) {
      const since = turnRef.current.closedAtMs === null ? 0 : Date.now() - turnRef.current.closedAtMs;
      voiceBeacon('post_close_cooldown_suppressed', { since_ms: since, path: 'cascade' });
      throw new Error('post_close_cooldown');
    }
    nextEpoch();
    wakeRemainderRef.current = null; // F2: a fresh fire never inherits a stale remainder
    turnRef.current.startWakeTurn(true); // quiet: nothing intelligible -> silent close
    armTurnOutcome();
    // IVG C4: NO visual/narration reset here -- a fire that the server then
    // rejects must leave the on-screen answer untouched. Deferred to confirmWakeTurn.
    await startListening(); // capture starts AT the fire, so "Hey Aura, <request>" in one breath is kept
  }, [startListening, nextEpoch, armTurnOutcome]);

  // WAKE CASCADE P1: confirmed -> the chime is the ONLY cue. The spoken
  // "Is there anything else?" is never played at a wake any more: the capture is
  // already open and the user is mid-sentence (owner, verbatim, 10-10-2026
  // ~17:05 UTC: "it midway of listening asked is there anything else?";
  // Option A 10-10-2026 15:21 UTC: "On a wake, Aura plays a short chime").
  const confirmWakeTurn = useCallback(async (opts?: { greet?: boolean; afterWake?: string | null }) => {
    // IVG C4: the UI changes only now that the wake is CONFIRMED.
    setAuraSections(null);
    cancelNarration();
    void player.unlock();
    const greet = opts?.greet === true;
    // F2: the confirm clip's after-wake words join this turn's first final.
    wakeRemainderRef.current = !greet && opts?.afterWake ? { epoch: epochRef.current, text: opts.afterWake } : null;
    if (greet) {
      // F7 (owner decision 10-10-2026 21:34 UTC): a LONE "Hey Aura" -> Aura
      // speaks "How can I help?" (the pre-recorded asset, zero provider cost),
      // THEN a FRESH capture opens. The pre-prompt capture held only the wake
      // word: it is DISCARDED (close() never transcribes). The hands-free glue
      // restarts the endpointer when this resolves (5 s to start speaking).
      // Never the retired "Is there anything else?".
      const greetEpoch = epochRef.current;
      void streamRef.current?.close();
      streamRef.current = null;
      if (stateRef.current.muted) {
        voiceBeacon('wake_greeting', { played: false, reason: 'muted', path: 'cascade' });
      } else {
        if (!greetingBytesRef.current) {
          try {
            const resp = await fetch(WAKE_GREETING_URL);
            if (resp.ok) greetingBytesRef.current = new Uint8Array(await resp.arrayBuffer());
          } catch (err) {
            voiceBeacon('wake_greeting_asset_failed', { message: (err as Error)?.message ?? 'unknown' });
          }
        }
        const bytes = greetingBytesRef.current;
        let played = false;
        if (bytes && bytes.byteLength > 0) {
          const outcome = await player.playBytes(bytes, 'audio/mpeg', {});
          played = outcome === 'played';
          if (played) {
            let t: ReturnType<typeof setTimeout> | null = null;
            await Promise.race([waitForPlaybackEnd(), new Promise<void>((r) => { t = setTimeout(r, WAKE_GREETING_MAX_WAIT_MS); })]);
            if (t) clearTimeout(t);
            turnRef.current.markWoken();
            // F9: the player reports "ended" before the speaker/room tail has died
            // (raw mic, EC off) -- open the capture only after a short guard.
            await new Promise<void>((r) => setTimeout(r, WAKE_GREETING_TAIL_GUARD_MS));
          }
        }
        voiceBeacon('wake_greeting', { played, path: 'cascade' });
      }
      if (isStale(greetEpoch, epochRef.current)) return; // PTT/stop took over meanwhile
      await startListening();
      return;
    }
    const prompt = selectWakePrompt(turnRef.current.hasWoken, turnRef.current.followUpPromptsSpoken);
    if (prompt.kind === 'tts') voiceBeacon('wake_followup_replaced_by_chime', { reason: 'capture_open' });
    if (stateRef.current.muted) {
      voiceBeacon('wake_chime', { played: false, reason: 'muted', path: 'cascade' });
      return;
    }
    const outcome = await player.playBytes(wakeChimeBytes(), WAKE_CHIME_MIME, {});
    voiceBeacon('wake_chime', { played: outcome === 'played', path: 'cascade' });
    if (outcome === 'played') turnRef.current.markWoken();
  }, [player, cancelNarration, waitForPlaybackEnd, startListening]);

  // WAKE CASCADE P1: not confirmed -> end the turn quietly, no cooldown stamp.
  const rejectWake = useCallback(() => {
    wakeRemainderRef.current = null;
    voiceBeacon('wake_rejected_silent', {});
    turnRef.current.endWakeTurn();
  }, []);

  // HANDS-FREE endpoint: end the one captured command (release semantics) so the
  // EXISTING STT -> assist -> TTS turn runs. finishListening leaves the borrowed
  // wake mic ALONE (micRef is null for the shared stream).
  const endWakeCommand = useCallback(async (): Promise<WakeTurnOutcome> => {
    dispatch({ type: 'PRESS_END' });
    const turn = turnOutcomeRef.current;
    await finishListening();                 // finalize capture -> STT onFinal -> think/clarify handlers
    const outcome = turn ? await turn.promise : 'done';
    if (outcome === 'clarify-capture') {
      armTurnOutcome();                      // arm the NEXT leg (the spoken answer)
      const capture = await startListening(); // open the follow-up STT on the shared mic (no greeting)
      if (!capture.ok) {
        // D1 FIX (voice-wave-4, owner re-test-2 2026-07-27): the clarify
        // follow-up leg failing to open capture must SURFACE, never silently
        // strand the controller mid-clarify while the wake bridge falls back to
        // bare 'armed' with the owner's spoken answer landing nowhere. Root
        // cause: this branch previously ignored startListening()'s {ok:false}
        // and unconditionally returned 'clarify' -- the bridge believed the
        // answer capture was open (CLARIFY_NEEDED/CLARIFY_SPOKEN -> 'capturing')
        // when it had never actually started (mic/permission/hardware error).
        // Diag-log + an explicit degraded state: fall back to the FULL briefing
        // -- the SAME no-answer policy already used for a genuine no-speech
        // clarify answer (handleBriefingWake), so the owner still gets an
        // audible answer instead of dead air, and settle the just-armed turn
        // outcome so a stray later resolve can never hang a future await.
        voiceBeacon('clarify_followup_capture_failed', {});
        turnRef.current.abandonClarify();
        settleTurn('done');
        void briefMe();
        return 'done';
      }
      return 'clarify';
    }
    turnRef.current.endWakeTurn();
    // voice residual #2: propagate 'empty' (no usable STT result) distinctly from
    // 'done' (real content) so the wake glue's empty-capture refractory can tell
    // the two apart — see EmptyCaptureRefractory in wake/wakeMachine.ts.
    return outcome === 'empty' ? 'empty' : 'done';
  }, [finishListening, startListening, armTurnOutcome, briefMe, settleTurn]);

  const setSharedMicStream = useCallback((stream: MediaStream | null) => {
    sharedMicRef.current = stream;
  }, []);

  // HANDS-FREE (iOS greeting fix): resume the shared AudioContext during the
  // Enable tap so a later wake-triggered greeting can play without a fresh
  // gesture (iOS Safari expires transient activation across the async gap).
  const primeAudioForHandsFree = useCallback(() => { void player.unlock(); }, [player]);

  const setWakeArmed = useCallback((armed: boolean) => {
    dispatch({ type: armed ? 'WAKE_ARMED' : 'WAKE_DISARMED' });
  }, []);

  const demoSay = useCallback((text: string) => {
    const s = streamRef.current;
    if (s instanceof MockSttStream) s.simulateUtterance(text);
  }, []);

  // LISTENING-PHASE BOUNDED TIMEOUT (P0 2026-07-20): guarantee "Listening"
  // ALWAYS terminates. While RECORDING (listening, not finalizing) a hard cap
  // auto-SENDS if a pointer-release is ever missed; while FINALIZING (release ->
  // transcript, STT in flight) a shorter guard RECOVERS if the round-trip
  // stalls. The instant status leaves 'listening' both are disarmed. Single-shot
  // via refs; NEITHER re-arms listening (the retired-watchdog regression class).
  useEffect(() => {
    const clearCap = () => {
      if (recordCapRef.current !== null) { clearTimeout(recordCapRef.current); recordCapRef.current = null; }
    };
    const clearGuard = () => {
      if (finalizeGuardRef.current !== null) { clearTimeout(finalizeGuardRef.current); finalizeGuardRef.current = null; }
    };
    if (state.status !== 'listening') { clearCap(); clearGuard(); return; }
    if (!state.finalizing) {
      // RECORDING phase: bound the hold so a missed release can't record forever.
      clearGuard();
      // PTT-ONLY (Phase A, voice-turn-audio-redesign 2026-07-27): this cap is
      // the PTT missed-release safety net and MUST NEVER re-arm for a
      // hands-free wake/clarify capture again -- that was the governor race
      // (this status-only timer, built for PTT recovery, was silently reused
      // for wake capture via the shared startListening()/'listening' status,
      // and could outrace CommandEndpointer with zero cross-awareness; see
      // docs/brain/product/voice-turn-audio-redesign-scope-2026-07-27.md §1).
      // A hands-free capture is now owned EXCLUSIVELY by CaptureWindow (one
      // clock, one terminal event -- app/src/voice/wake/captureWindow.ts).
      // turnRef.current.handsFreeTurn is true for the WHOLE wake turn (initial
      // command capture + any clarify follow-up leg) and false for a genuine
      // PTT press (pressToTalk clears it up-front, above) and false again once
      // a wake turn settles (endWakeCommand) -- so this is a reliable PTT-vs-
      // hands-free signal (Phase B: entity-owned boolean, was handsFreeTurnRef).
      if (turnRef.current.handsFreeTurn) { clearCap(); return; }
      if (recordCapRef.current === null) {
        recordCapRef.current = setTimeout(() => {
          recordCapRef.current = null;
          if (listenTimeoutAction(stateRef.current.status, stateRef.current.finalizing, streamRef.current !== null) !== 'send') return;
          // A release was never delivered — auto-submit what was captured.
          pressActiveRef.current = false;
          releasePendingRef.current = false;
          dispatch({ type: 'PRESS_END' });
          void finishListening();
        }, LISTEN_RECORD_CAP_MS);
      }
    } else {
      // FINALIZING phase: bound the STT round-trip so a stall can't hang.
      clearCap();
      if (finalizeGuardRef.current === null) {
        finalizeGuardRef.current = setTimeout(() => {
          finalizeGuardRef.current = null;
          if (listenTimeoutAction(stateRef.current.status, stateRef.current.finalizing, streamRef.current !== null) !== 'recover') return;
          // The transcribe round-trip stalled — abort it and recover (never hang).
          pressActiveRef.current = false;
          releasePendingRef.current = false;
          releaseMic(); // close()s the STT stream (aborts the in-flight upload) + stops the mic
          dispatch({ type: 'ERROR', error: STT_TIMEOUT_ERROR });
        }, STT_FINALIZE_TIMEOUT_MS);
      }
    }
  }, [state.status, state.finalizing, finishListening, releaseMic]);

  useEffect(
    () => () => {
      if (recordCapRef.current !== null) clearTimeout(recordCapRef.current);
      if (finalizeGuardRef.current !== null) clearTimeout(finalizeGuardRef.current);
      cancelNarration('unmount');
      releaseMic();
      player.dispose();
    },
    [releaseMic, player, cancelNarration],
  );

  // IVG FINDING-1 BACKSTOP (2026-07-27): `answering` must NEVER be stuck true
  // with no live playback. The cancelNarration choke point (above) closes the
  // window on every known cancel path; this watchdog is defense in depth for
  // any path that does not (e.g. a superseding speakSegments() run whose
  // replacement queue turns out fully blocked/empty -- its inline
  // narrator.cancel() bypasses cancelNarration by design, normally re-opening
  // the window itself via onSpeakStart). "Live" = the player is actually
  // playing OR a SegmentedNarrator run is active (the narrator stays active
  // through its 700ms inter-segment pauses, so legitimate gaps never count as
  // stuck). After WAKE_ANSWERING_BACKSTOP_MS of continuous answering-with-
  // nothing-live, force-close the window and beacon LOUDLY -- never a silent
  // clear. Evaluated inside isAnswering() because the wake reducer's 250ms
  // hold/retry loop polls exactly this function -- the loop that would
  // otherwise spin forever is the thing that heals itself.
  const answeringStuckSinceRef = useRef<number | null>(null);
  // C2 (stop-fix IVG 10-10-2026): diag-OFF no-speech discard of a QUIET (capped)
  // wake turn closes it quietly -- the same end state as the diag-ON path
  // (onFinal('') -> classifyFinal 'quiet_close'), so the next genuine wake after
  // the cooldown greets fresh instead of staying silent all day. Logged, never
  // silent: the same beacon as the diag-ON quiet close, tagged with the path.
  const noSpeechDiscard = useCallback((): void => {
    wakeRemainderRef.current = null; // Fable C-AO1: the wake turn ended without a final
    const t = turnRef.current;
    if (!t.quietTurn || !t.handsFreeTurn) return;
    voiceBeacon('followup_cap_quiet_close', { path: 'no_speech_discard' });
    t.close(Date.now());
  }, []);

  const isAnswering = useCallback((): boolean => {
    const t = turnRef.current;
    const nowMs = Date.now();
    if (t.answering) {
      const live = player.getState().playing || (narratorRef.current?.isPlaying() ?? false);
      if (live) {
        answeringStuckSinceRef.current = null;
      } else if (answeringStuckSinceRef.current === null) {
        answeringStuckSinceRef.current = nowMs;
      } else if (nowMs - answeringStuckSinceRef.current >= WAKE_ANSWERING_BACKSTOP_MS) {
        const stuckMs = nowMs - answeringStuckSinceRef.current;
        t.endAnswering(nowMs);
        answeringStuckSinceRef.current = null;
        voiceBeacon('wake_answering_backstop_cleared', { stuck_ms: stuckMs });
      }
    } else {
      answeringStuckSinceRef.current = null;
    }
    return t.isAnsweringOrGrace(nowMs);
  }, [player]);

  return {
    state,
    persona,
    auraSpeech: auraSpeechRef.current,
    actionReady: isActionAuthorised(state),
    approvedAction: state.approvedAction,
    needsPlaybackTap,
    auraSections,
    briefingLoading,
    briefMe,
    presentBriefing,
    speakSegments,
    replay,
    replayLast,
    canReplay: canReplayLast(state.status, retained),
    pressToTalk,
    releaseToTalk,
    cancelToTalk,
    stop,
    mute: () => dispatch({ type: 'MUTE' }),
    unmute: () => dispatch({ type: 'UNMUTE' }),
    interrupt,
    submitText,
    requestApproval: (action) => dispatch({ type: 'REQUEST_APPROVAL', action }),
    approve: () => dispatch({ type: 'APPROVE' }),
    reject: () => dispatch({ type: 'REJECT' }),
    clearApprovedAction: () => dispatch({ type: 'CLEAR_APPROVED_ACTION' }),
    setSharedMicStream,
    setWakeArmed,
    setPttObserver,
    beginWakeTurn,
    endWakeCommand,
    primeAudioForHandsFree,
    beginPublishConfirm,
    batchPublish: batch.view,
    confirmBatchPublish: (mode: BatchMode, draftIds?: number[]) => batch.confirm(mode, draftIds, 'tap'),
    chooseBatchSite: batch.chooseSite,
    undoBatchPublish: batch.undo,
    dismissBatchPublish: batch.dismiss,
    updateLivePost: batch.updateLive,
    confirmUpdateLivePost: () => batch.confirmUpdateLive('tap'),
    cancelUpdateLivePost: batch.cancelUpdateLive,
    undoUpdateLivePost: batch.undoUpdateLive,
    suggestWrite: suggest.view,
    writeSuggested: () => suggest.writeAll('tap'),
    writeSuggestedOne: (index: number) => suggest.writeOne(index, 'tap'),
    chooseSuggestSite: suggest.chooseSite,
    dismissSuggestWrite: suggest.dismiss,
    recommend: recommend.view,
    pickRecommended: recommend.pick,
    pickAllRecommended: recommend.pickAll,
    chooseRecommendSite: recommend.chooseSite,
    dismissRecommend: recommend.dismiss,
    demoSay,
    // Phase C (self-trigger fix): live read of the turn entity's TRUE
    // playback-in-flight-or-grace signal, exposed for the HandsFreeBridge
    // (see turnEntity.isAnsweringOrGrace / HandsFreeBridge.isAnswering).
    isAnswering,
    noSpeechDiscard,
    beginSilentWakeTurn,
    confirmWakeTurn,
    rejectWake,
  };
}
